/**
 * broqueleria-motion.js — La Broquelería
 * ---------------------------------------------------------------
 * Motor de movimiento del tema (vanilla JS, sin dependencias).
 * - Reveals al scroll (fade-up + stagger) vía IntersectionObserver
 * - Auto-decorado prudente de módulos clave de Horizon
 * - Sparkles decorativos de 4 puntas (eco del logo) en [data-broq-sparkles]
 * - Respeta prefers-reduced-motion (todo visible, sin animación)
 * - Compatible con el editor de temas (shopify:section:load)
 *
 * Defer-safe e idempotente: puede cargarse con `defer` y ejecutarse
 * varias veces sin duplicar observers ni decoraciones.
 *
 * Convención CSS (broqueleria-motion.css):
 *   html.broq-motion-ready .broq-reveal          -> oculto (opacity 0, translateY 24px)
 *   html.broq-motion-ready .broq-reveal.is-visible -> visible (transición con var(--broq-delay))
 *   .broq-sparkle                                 -> destello 4 puntas con twinkle
 * ---------------------------------------------------------------
 */
(function () {
  'use strict';

  // Guardia de idempotencia global (permite re-invocar init sin duplicar estado)
  if (window.BroqueleriaMotion && window.BroqueleriaMotion.__installed) return;

  var STAGGER_MS = 80;      // incremento de retraso entre hermanos
  var STAGGER_CAP = 6;      // tope: 6 * 80ms = 480ms (grids grandes no esperan eternamente)
  var THRESHOLD = 0.15;     // umbral principal del observer
  var SPARKLE_DEFAULT = 3;  // sparkles por elemento (2-3)

  var reducedMotionQuery = window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)')
    : { matches: false, addEventListener: null };

  /**
   * Base de assets del tema, derivada del src de este mismo script
   * (p. ej. https://cdn.shopify.com/.../assets/broqueleria-motion.js?v=1
   *  -> https://cdn.shopify.com/.../assets/). Permite cargar el isotipo
   * (broq-isotipo.png) sin necesitar Liquid.
   */
  var ASSET_BASE = (function () {
    var src = document.currentScript && document.currentScript.src;
    if (!src) {
      var tag = document.querySelector('script[src*="broqueleria-motion"]');
      src = tag ? tag.src : '';
    }
    return src ? src.replace(/broqueleria-motion\.js.*$/, '') : '';
  })();

  var HEADER_COMPACT_THRESHOLD = 120; // px de scroll para compactar el logo

  /**
   * Selectores prudentes de Horizon (existen en el tema real; si no
   * están presentes en la página, querySelectorAll devuelve vacío y
   * no pasa nada — nunca rompemos).
   * El orden importa: contenedores antes que sus hijos, para que la
   * guardia de anidado evite dobles reveals.
   */
  var AUTO_SELECTORS = [
    '.section-resource-list__header',   // encabezados de secciones con listas de recursos
    '.main-collection-grid__title',     // título de la colección
    '.custom-section-content',          // contenido de secciones custom
    '.resource-list__item',             // items de grids (productos, colecciones, blogs)
    '.product-card',                    // cards de producto sueltas
    '.collection-card',                 // cards de colección sueltas
    '.blog-post-card',                  // cards de blog sueltas
    '.jumbo-text',                      // texto jumbo
    '.text-block'                       // bloques de texto / headings
  ];

  /** Zonas donde NUNCA auto-decoramos (header, drawers, modales, opt-out). */
  var EXCLUDE_CLOSEST = [
    'header',
    '#header-component',
    'header-component',
    '.header',
    'cart-drawer',
    '.cart-drawer',
    'quick-add-dialog',
    'dialog',
    '[role="dialog"]',
    '.announcement-bar',
    'announcement-bar-component',
    '[data-broq-no-reveal]'
  ].join(',');

  var observer = null;      // observer principal (threshold .15)
  var tallObserver = null;  // observer para elementos más altos que el viewport

  /* ------------------------------------------------------------ */
  /* Utilidades                                                    */
  /* ------------------------------------------------------------ */

  function prefersReducedMotion() {
    return !!reducedMotionQuery.matches;
  }

  /** Pseudo-aleatorio determinístico: mismo índice -> misma posición siempre. */
  function seededFraction(seed) {
    var x = Math.sin(seed * 127.1 + 311.7) * 43758.5453123;
    return x - Math.floor(x);
  }

  function safeQueryAll(root, selector) {
    try {
      return root.querySelectorAll(selector);
    } catch (e) {
      return []; // selector inválido en algún navegador viejo: no romper
    }
  }

  function revealNow(el) {
    el.classList.add('is-visible');
  }

  /* ------------------------------------------------------------ */
  /* 1) IntersectionObserver + stagger                             */
  /* ------------------------------------------------------------ */

  function ensureObservers() {
    if (observer || !('IntersectionObserver' in window)) return;

    var onIntersect = function (entries, obs) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (entry.isIntersecting) {
          revealNow(entry.target);
          obs.unobserve(entry.target); // una sola vez
        }
      }
    };

    observer = new IntersectionObserver(onIntersect, { threshold: THRESHOLD });
    // Elementos muy altos nunca alcanzan ratio .15: usar umbral mínimo.
    tallObserver = new IntersectionObserver(onIntersect, { threshold: 0.01 });
  }

  /** Asigna --broq-delay incremental (80ms) entre hermanos .broq-reveal del mismo padre. */
  function applyStagger(root) {
    var reveals = safeQueryAll(root, '.broq-reveal');
    var groups = []; // pares [parent, count] — sin Map para máxima compatibilidad

    for (var i = 0; i < reveals.length; i++) {
      var el = reveals[i];
      if (el.classList.contains('is-visible')) continue; // ya revelado: no tocar
      var parent = el.parentElement;
      if (!parent) continue;

      var index = -1;
      for (var g = 0; g < groups.length; g++) {
        if (groups[g][0] === parent) { index = g; break; }
      }
      if (index === -1) {
        groups.push([parent, 0]);
        index = groups.length - 1;
      }

      var position = groups[index][1];
      var delay = Math.min(position, STAGGER_CAP) * STAGGER_MS;
      el.style.setProperty('--broq-delay', delay + 'ms');
      groups[index][1] = position + 1;
    }
  }

  /** Registra en el observer todos los .broq-reveal aún no observados. */
  function observeReveals(root) {
    var reveals = safeQueryAll(root, '.broq-reveal');

    for (var i = 0; i < reveals.length; i++) {
      var el = reveals[i];
      if (el.getAttribute('data-broq-observed') === 'true') continue;
      el.setAttribute('data-broq-observed', 'true');

      if (el.classList.contains('is-visible')) continue;

      if (!observer) {
        // Sin IntersectionObserver (navegador muy viejo): mostrar de inmediato.
        revealNow(el);
        continue;
      }

      var rect;
      try { rect = el.getBoundingClientRect(); } catch (e) { rect = null; }
      var viewportH = window.innerHeight || document.documentElement.clientHeight || 0;

      if (rect && viewportH && rect.height > viewportH * 0.85) {
        tallObserver.observe(el);
      } else {
        observer.observe(el);
      }
    }
  }

  /* ------------------------------------------------------------ */
  /* 2) Auto-decorado de módulos clave de Horizon                  */
  /* ------------------------------------------------------------ */

  function autoDecorate(root) {
    for (var s = 0; s < AUTO_SELECTORS.length; s++) {
      var nodes = safeQueryAll(root, AUTO_SELECTORS[s]);

      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (el.classList.contains('broq-reveal')) continue;

        // Nunca dentro de header, drawers, modales o zonas con opt-out.
        try {
          if (el.closest && el.closest(EXCLUDE_CLOSEST)) continue;
          // Evitar reveals anidados (el contenedor ya animará a sus hijos).
          var ancestor = el.parentElement && el.parentElement.closest
            ? el.parentElement.closest('.broq-reveal')
            : null;
          if (ancestor) continue;
        } catch (e) {
          continue; // cualquier duda: no decorar, no romper
        }

        el.classList.add('broq-reveal');
      }
    }
  }

  /* ------------------------------------------------------------ */
  /* 3) Sparkles decorativos en [data-broq-sparkles]               */
  /* ------------------------------------------------------------ */

  function decorateSparkles(root) {
    var hosts = safeQueryAll(root, '[data-broq-sparkles]');

    for (var h = 0; h < hosts.length; h++) {
      var host = hosts[h];
      if (host.getAttribute('data-broq-sparkles-init') === 'true') continue;
      host.setAttribute('data-broq-sparkles-init', 'true');

      // El host necesita ser contexto de posicionamiento.
      try {
        var computed = window.getComputedStyle(host);
        if (computed && computed.position === 'static') {
          host.style.position = 'relative';
        }
      } catch (e) { /* seguir sin ajustar posición */ }

      var requested = parseInt(host.getAttribute('data-broq-sparkles'), 10);
      var count = requested >= 2 && requested <= 3 ? requested : SPARKLE_DEFAULT;

      for (var k = 0; k < count; k++) {
        // Posiciones determinísticas por índice de host + índice de sparkle
        // (nada de Math.random: mismo layout en cada carga).
        var seed = h * 31 + k * 7 + 3;
        var top = 8 + seededFraction(seed) * 70;            // 8% – 78%
        var left = 6 + seededFraction(seed + 13) * 86;      // 6% – 92%
        var size = 8 + Math.round(seededFraction(seed + 29) * 8);  // 8px – 16px
        var delay = Math.round(seededFraction(seed + 47) * 2400);  // 0 – 2400ms

        var sparkle = document.createElement('span');
        sparkle.className = 'broq-sparkle';
        sparkle.setAttribute('aria-hidden', 'true');
        sparkle.style.top = top.toFixed(1) + '%';
        sparkle.style.left = left.toFixed(1) + '%';
        sparkle.style.setProperty('--broq-sparkle-size', size + 'px');
        sparkle.style.setProperty('--broq-twinkle-delay', delay + 'ms');
        host.appendChild(sparkle);
      }
    }
  }

  /* ------------------------------------------------------------ */
  /* 4) Header compacto: logotipo completo <-> isotipo B al scroll */
  /*    Funcional (ahorra espacio): activo también con PRM; solo   */
  /*    las transiciones se apagan vía CSS.                        */
  /* ------------------------------------------------------------ */

  var headerScrollBound = false;
  var headerScrollTicking = false;

  function updateHeaderCompact() {
    headerScrollTicking = false;
    var compact = (window.scrollY || window.pageYOffset || 0) > HEADER_COMPACT_THRESHOLD;
    document.documentElement.classList.toggle('broq-header-compact', compact);
  }

  function onHeaderScroll() {
    if (headerScrollTicking) return;
    headerScrollTicking = true;
    window.requestAnimationFrame(updateHeaderCompact);
  }

  function initHeaderCompact() {
    var logo = document.querySelector('a.header-logo');
    if (!logo || !ASSET_BASE) return;

    if (!logo.querySelector('.broq-isotipo-container')) {
      var span = document.createElement('span');
      span.className = 'broq-isotipo-container';
      span.setAttribute('aria-hidden', 'true');
      var img = document.createElement('img');
      img.className = 'broq-isotipo-img';
      img.src = ASSET_BASE + 'broq-isotipo.png';
      img.alt = '';
      img.loading = 'eager';
      span.appendChild(img);
      logo.appendChild(span);
    }

    if (!headerScrollBound) {
      headerScrollBound = true;
      window.addEventListener('scroll', onHeaderScroll, { passive: true });
      updateHeaderCompact();
    }
  }


  /* ------------------------------------------------------------ */
  /* 6) Descarga de fotos con captura de lead (Klaviyo)            */
  /*    - Logueado o lead ya capturado -> descarga directa.        */
  /*    - Si no: modal de correo -> POST a Klaviyo -> descarga.    */
  /* ------------------------------------------------------------ */

  var LEAD_KEY = 'broq_lead_email';

  // broqDownloadAll (descarga directa de las fotos sueltas) se retiró el
  // 19-oct-2026: las fotos se mandan solo por correo (decisión de Sergio).

  // ── MARTECH · evento Lead → Meta ──────────────────────────────────────
  // Brief: martech/implementacion/07-evento-lead.md
  //
  // El wrapper de Signals Gateway intercepta fbq('track', ...) y lo manda
  // por navegador Y por servidor, deduplicado. NO se pasa eventID a mano:
  // el wrapper (forkFromSnippetCode@1.0) genera el suyo al bifurcar, y uno
  // manual rompería su deduplicación.
  //
  // `perfil_negocio` va SIEMPRE, incluso como 'no-declarado': la custom
  // conversion `LB – Lead MAYOREO` filtra por "distinto de personal", y un
  // parámetro ausente no cumpliría esa regla.
  //
  // El aviso de privacidad declara el envío cifrado a Meta. BroqMatching
  // además exige marketingAllowed y saleOfDataAllowed de Shopify.
  // broqSetUserData prepara la identidad de la siguiente página; no añade
  // identificadores retroactivos al Lead de esta carga.
  function broqTrackLead(origen, extras) {
    try {
      if (typeof window.fbq !== 'function') return;
      var props = { lead_origen: origen, perfil_negocio: 'no-declarado' };
      if (extras) {
        for (var k in extras) {
          if (Object.prototype.hasOwnProperty.call(extras, k) && extras[k]) props[k] = extras[k];
        }
      }
      window.fbq('track', 'Lead', props);
    } catch (e) { /* nunca romper un form por tracking */ }
  }

  // Guarda hashes consentidos de la sesión para el primer init de la
  // siguiente página. Ver propuestas/2026-10-02-advanced-matching.md.
  function broqSetUserData(d) {
    try {
      if (window.BroqMatching) window.BroqMatching.remember(d).catch(function () {});
    } catch (e) { /* nunca romper un form por tracking */ }
  }

  // 🔴 El teléfono TIENE que normalizarse igual que en el servidor o los dos
  // hashes no coinciden y Meta ve dos personas. El Flow manda E.164 sin
  // símbolos (52 + 10 dígitos, porque Shopify guarda "+52…"); los forms
  // capturan 10 dígitos pelones. Aquí se empatan.
  function broqTelE164(tel) {
    var d = String(tel).replace(/\D/g, '');
    if (d.length === 10) d = '52' + d;
    return d;
  }

  // opts: { companyId, listId, email, phone (10 dígitos MX, sin +52),
  //         customSource, properties }
  // Consentimiento: email → canal email; phone → canal WHATSAPP (decisión
  // de Sergio 30-jul-2026: cero SMS, no se usa en México). OJO: mientras
  // WhatsApp no esté conectado en la cuenta de Klaviyo, ese consent se
  // descarta en silencio (202 pero el job lo ignora — mismo comportamiento
  // verificado con SMS); por eso el quiz hace además un upsert directo de
  // perfil que guarda la evidencia del consentimiento en properties.
  function broqSubscribeKlaviyo(opts) {
    var attributes = { properties: opts.properties || {} };
    if (opts.firstName) attributes.first_name = opts.firstName;
    if (opts.lastName) attributes.last_name = opts.lastName;
    var subscriptions = {};
    if (opts.email) {
      attributes.email = opts.email;
      subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
    }
    if (opts.phone) {
      attributes.phone_number = '+52' + opts.phone.replace(/\D/g, '');
      // optInWhatsapp: false cuando el texto legal mostrado a la clienta
      // no mencionó WhatsApp — el número se guarda como dato de perfil,
      // pero no se marca un consentimiento de marketing que nunca se pidió.
      if (opts.optInWhatsapp !== false) {
        subscriptions.whatsapp = { marketing: { consent: 'SUBSCRIBED', consented_at: new Date().toISOString() } };
      }
    }
    attributes.subscriptions = subscriptions;

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: opts.customSource || 'Sitio',
          profile: { data: { type: 'profile', attributes: attributes } }
        },
        relationships: { list: { data: { type: 'list', id: opts.listId } } }
      }
    };
    return fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(opts.companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2025-01-15' },
      body: JSON.stringify(payload)
    });
  }

  // Evento "Descargó fotos" — se dispara SIEMPRE que alguien descarga,
  // sea lead nuevo, lead que vuelve o clienta logueada. Antes del 13-ago
  // la descarga de una repetidora o de alguien logueado no dejaba ningún
  // rastro: el gate las mandaba directo a `descargar()` sin pasar por
  // Klaviyo (ver el `if (loggedIn || leadPrevio)` de más abajo). Con esto
  // se puede saber qué producto se descarga más y quién vuelve por más
  // fotos, que es justo la señal B2B que antes se perdía.
  //
  // opts: { companyId, email, externalId, producto, sku, zipUrl }
  // Se manda email O externalId (nunca ninguno: sin identidad no hay a
  // quién atribuirle el evento, y se prefiere no mandar nada a mandar un
  // evento huérfano que ensucie las métricas).
  // Las URLs del CDN de Shopify salen de Liquid sin protocolo ("//cdn…"):
  // en un correo eso es un enlace roto (hallazgo de retention, 19-oct-2026).
  function broqUrlAbsoluta(u) {
    u = String(u || '');
    if (/^\/\//.test(u)) return 'https:' + u;
    return u;
  }

  // 22-oct-2026 (retention): 3 eventos de septiembre/octubre salieron con
  // zip_url vacío (L009 y 30497 aún sin custom.pack_fotos; el metafield se
  // creó el 18-oct) y el flujo TdJZQT mandó el correo con el botón a ninguna
  // parte. Sin un zip válido NO se manda "Descargó fotos" (ese evento dispara
  // el correo); en su lugar va "Descarga sin zip" para que retention lo vea
  // y reenvíe a mano, más un aviso en consola.
  function broqZipValido(u) {
    return /^https:\/\/[^\s\/?#]+\/\S*\.zip(\?\S*)?$/i.test(String(u || ''));
  }

  function broqTrackDownloadEvent(opts) {
    if (!opts.email && !opts.externalId) return;
    var zipUrl = broqUrlAbsoluta(opts.zipUrl);
    var zipOk = broqZipValido(zipUrl);
    var profileAttrs = {};
    if (opts.email) profileAttrs.email = opts.email;
    if (opts.externalId) profileAttrs.external_id = String(opts.externalId);
    // 19-oct-2026: desde hoy las fotos van SOLO por correo (flujo de Klaviyo
    // "Entrega — zip de fotos y video" sobre esta métrica). Si el producto no
    // tiene pack (zip_url vacío), el correo necesita algo que enlazar: van las
    // URLs de las fotos de la ficha, absolutas, separadas por coma.
    var fotos = [];
    if (opts.fotos && opts.fotos.length) {
      for (var i = 0; i < opts.fotos.length; i++) fotos.push(broqUrlAbsoluta(opts.fotos[i]));
    }
    var payload = {
      data: {
        type: 'event',
        attributes: {
          properties: {
            producto: opts.producto || '',
            producto_titulo: opts.productoTitulo || '',
            producto_url: opts.productoUrl || '',
            sku: opts.sku || '',
            zip_url: zipOk ? zipUrl : '',
            tiene_zip: zipOk,
            fotos_urls: fotos.join(', '),
            fotos_count: fotos.length
          },
          metric: { data: { type: 'metric', attributes: { name: zipOk ? 'Descargó fotos' : 'Descarga sin zip' } } },
          profile: { data: { type: 'profile', attributes: profileAttrs } },
          time: new Date().toISOString()
        }
      }
    };
    if (!zipOk) {
      try { console.warn('[broq] descarga sin zip válido: no se dispara el correo de fotos', { producto: opts.producto || '', sku: opts.sku || '' }); } catch (e) { /* ok */ }
    }
    try {
      fetch('https://a.klaviyo.com/client/events/?company_id=' + encodeURIComponent(opts.companyId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2025-01-15' },
        body: JSON.stringify(payload)
      });
    } catch (e) { /* nunca romper la descarga por tracking */ }
  }

  function initDownloadGate(root) {
    var botones = safeQueryAll(root, '[data-broq-download]');

    for (var i = 0; i < botones.length; i++) {
      (function (btn) {
        if (btn.getAttribute('data-broq-dl-init') === 'true') return;
        btn.setAttribute('data-broq-dl-init', 'true');

        var modal = document.querySelector('[data-broq-modal]');
        var urls;
        try { urls = JSON.parse(btn.getAttribute('data-images') || '[]'); } catch (e) { urls = []; }
        var handle = btn.getAttribute('data-product-handle') || 'producto';
        var productoTitulo = btn.getAttribute('data-product-title') || '';
        var productoUrl = btn.getAttribute('data-product-url') || '';
        var sku = btn.getAttribute('data-sku') || '';
        var zipUrl = btn.getAttribute('data-zip-url') || '';
        // El company-id (cuenta de Klaviyo Rn3iCy) y la lista se leen del botón
        // al enviar el formulario, más abajo; data-customer-id y data-logged-in
        // siguen en el markup por si martech los necesita, el gate ya no los usa.

        // 19-oct-2026 (Sergio, 11:35): las fotos se mandan SOLO por correo.
        // Ya no hay descarga directa (ni del zip ni del loop de imágenes):
        // SIEMPRE se abre el formulario, aunque la clienta ya sea lead o esté
        // logueada (se le prellena el correo). El evento "Descargó fotos"
        // lleva zip_url para que el flujo de Klaviyo que manda el correo tenga
        // qué adjuntar. Quien no tiene gate configurado tampoco descarga.
        btn.addEventListener('click', function () {
          if (!modal) return;
          var leadPrevio = null;
          try { leadPrevio = window.localStorage.getItem(LEAD_KEY); } catch (e) { /* modo privado */ }
          var form0 = modal.querySelector('[data-broq-lead-form]');
          var listo0 = modal.querySelector('[data-broq-lead-listo]');
          if (form0) form0.hidden = false;
          if (listo0) listo0.hidden = true;
          modal.hidden = false;
          document.documentElement.classList.add('broq-modal-abierto');
          var input = modal.querySelector('input[type="email"]');
          if (input) {
            if (leadPrevio && !input.value) input.value = leadPrevio;
            input.focus();
          }
        });

        if (modal && modal.getAttribute('data-broq-modal-init') !== 'true') {
          modal.setAttribute('data-broq-modal-init', 'true');

          function cerrar() {
            modal.hidden = true;
            document.documentElement.classList.remove('broq-modal-abierto');
          }
          function mostrarListo() {
            var f = modal.querySelector('[data-broq-lead-form]');
            var l = modal.querySelector('[data-broq-lead-listo]');
            if (f) f.hidden = true;
            if (l) { l.hidden = false; var b = l.querySelector('button'); if (b) b.focus(); }
          }
          var cierres = modal.querySelectorAll('[data-broq-modal-close]');
          for (var c = 0; c < cierres.length; c++) cierres[c].addEventListener('click', cerrar);
          document.addEventListener('keydown', function (ev) {
            if (ev.key === 'Escape' && !modal.hidden) cerrar();
          });

          var form = modal.querySelector('[data-broq-lead-form]');
          var errorEl = modal.querySelector('[data-broq-lead-error]');
          if (form) {
            form.addEventListener('submit', function (ev) {
              ev.preventDefault();
              var email = (form.email.value || '').trim();
              var telefono = (form.telefono ? form.telefono.value : '').trim();
              if (!email || !telefono) return;
              var submitBtn = form.querySelector('button[type="submit"]');
              if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Enviando…'; }
              if (errorEl) errorEl.hidden = true;

              broqSubscribeKlaviyo({
                companyId: btn.getAttribute('data-company-id'),
                listId: btn.getAttribute('data-list-id'),
                email: email,
                phone: telefono,
                customSource: 'PDP descarga fotos',
                properties: { origen: 'descarga-fotos', producto: handle, marca: 'La Broqueleria' }
              })
                .then(function (r) {
                  if (!r.ok && r.status !== 202) throw new Error('klaviyo ' + r.status);
                  try { window.localStorage.setItem(LEAD_KEY, email); } catch (e) { /* ok */ }
                  // Sin checkbox de consent: lo cubre el aviso de privacidad.
                  broqSetUserData({ email: email, phone: telefono });
                  // Sin perfil_negocio declarado → queda 'no-declarado', y
                  // cuenta como ICP a propósito: pedir las fotos del producto
                  // es la señal B2B más fuerte del sitio.
                  broqTrackLead('descarga-fotos', { producto: handle });
                  broqTrackDownloadEvent({
                    companyId: btn.getAttribute('data-company-id'),
                    email: email,
                    producto: handle,
                    productoTitulo: productoTitulo,
                    productoUrl: productoUrl,
                    sku: sku,
                    zipUrl: zipUrl,
                    fotos: urls
                  });
                  // Sin descarga: la confirmación dice que van por correo.
                  mostrarListo();
                })
                .catch(function () {
                  if (errorEl) errorEl.hidden = false;
                })
                .then(function () {
                  if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Enviarme las fotos'; }
                });
            });
          }
        }
      })(botones[i]);
    }
  }

  /* ------------------------------------------------------------ */
  /* 5) prefers-reduced-motion: todo visible, sin animar           */
  /* ------------------------------------------------------------ */

  function revealEverything(root) {
    var reveals = safeQueryAll(root, '.broq-reveal');
    for (var i = 0; i < reveals.length; i++) {
      revealNow(reveals[i]);
    }
  }

  /* ------------------------------------------------------------ */
  /* Inicialización                                                */
  /* ------------------------------------------------------------ */

  /**
   * Formato de dinero de la tienda, con centavos (22-oct-2026, Sergio: "se
   * que son centavos pero tiene que cuadrar"). Espejo de `money_without_
   * trailing_zeros` con moneyFormat "${{amount}}": $541.79, $1,234.50,
   * $542 (solo se quitan los ".00"). Recibe PESOS (puede traer decimales);
   * se pasa a centavos enteros para no arrastrar ruido de punto flotante.
   * NUNCA redondea al peso: el precio que se ve es el que se cobra.
   */
  function broqMoney(pesos) {
    var c = Math.round(Number(pesos) * 100);
    if (!isFinite(c)) return '$0';
    var neg = c < 0;
    if (neg) c = -c;
    var ent = Math.floor(c / 100);
    var dec = c % 100;
    var txt = String(ent).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (neg ? '-' : '') + '$' + txt + (dec ? '.' + (dec < 10 ? '0' : '') + dec : '');
  }

  /**
   * Actualiza el MSI (6 meses) y el precio de mayoreo al cambiar de
   * variante (p. ej. Pieza / Par), ya que esos strips se renderizan del
   * lado del servidor con el precio de la variante por defecto.
   * Lee el mapa variantId->centavos que el snippet imprime en
   * [data-broq-variant-prices]. Funcional: corre siempre (incluso PRM).
   */
  function broqSyncDiscountBadge(badge, state) {
    if (!badge) return;
    state = state || {};
    var data = badge.dataset;
    if (state.percent !== undefined) data.broqDiscountPercent = state.percent == null ? '' : String(state.percent);
    if (state.variantId !== undefined) data.broqVariantId = String(state.variantId);
    if (state.price !== undefined) data.broqDiscountPrice = String(state.price);
    if (state.reference !== undefined) data.broqDiscountReference = state.reference == null ? '' : String(state.reference);
    if (state.eligible !== undefined) data.broqDiscountEligible = String(state.eligible === true);
    if (state.kitVerified !== undefined) data.broqKitVerified = String(state.kitVerified === true);
    if (state.requiresKit !== undefined) {
      if (state.requiresKit) data.broqKitVerification = 'required';
      else delete data.broqKitVerification;
    }
    var percent = Number(data.broqDiscountPercent);
    var valid = Number.isInteger(percent) && percent >= 1 && percent <= 99;
    var waiting = data.broqKitVerification === 'required' && data.broqKitVerified !== 'true';
    var hidden = !valid || data.broqDiscountEligible !== 'true' || waiting;
    if (badge.hidden !== hidden) badge.hidden = hidden;
    var text = valid ? '-' + percent + '% OFF' : '';
    if (badge.textContent !== text) badge.textContent = text;
    var label = valid ? percent + '% de descuento respecto al precio sugerido de venta en menudeo' : '';
    if (badge.getAttribute('aria-label') !== label) badge.setAttribute('aria-label', label);
  }
  window.BroqDiscountBadges = { sync: broqSyncDiscountBadge };

  var kitBadgeModule;
  function initDiscountBadges(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var pendingKit;
    scope.querySelectorAll('[data-broq-discount-badge]').forEach(function (badge) {
      broqSyncDiscountBadge(badge);
      if (badge.dataset.broqKitVerification === 'required' && badge.closest('[data-broq-kitv2]')) badge.dataset.broqKitVerificationStarted = 'pdp';
      if (!pendingKit && badge.dataset.broqKitVerification === 'required' && !badge.closest('[data-broq-kitv2]')) pendingKit = badge;
    });
    if (!pendingKit || !pendingKit.dataset.broqKitModule) return;
    if (!kitBadgeModule) kitBadgeModule = new Promise(function (resolve, reject) {
      if (window.BroqKitDiscountBadges) { resolve(window.BroqKitDiscountBadges); return; }
      // Shopify transforma import() dentro de este asset clásico. Cargar un
      // módulo nativo conserva el mismo contrato sin depender de require().
      var script = document.createElement('script');
      script.type = 'module';
      script.src = pendingKit.dataset.broqKitModule;
      script.onload = function () {
        if (window.BroqKitDiscountBadges) resolve(window.BroqKitDiscountBadges);
        else reject(new Error('Kit badge module unavailable'));
      };
      script.onerror = function () { reject(new Error('Kit badge module unavailable')); };
      document.head.appendChild(script);
    });
    kitBadgeModule.then(function (module) { return module.bootstrap(scope); }).catch(function () { /* Sigue oculto si no se puede verificar. */ });
  }

  function initVariantPriceStrips(root) {
    var host = (root && root.querySelector ? root : document).querySelector('[data-broq-variant-prices]')
      || document.querySelector('[data-broq-variant-prices]');
    if (!host) return;
    var productScope = host.closest('.shopify-section') || document;
    var discountHost = productScope.querySelector('[data-broq-discount-data]');
    var discounts = {};
    if (discountHost) { try { discounts = JSON.parse(discountHost.textContent || '{}'); } catch (e) {} }
    var prices;
    try { prices = JSON.parse(host.textContent || '{}'); } catch (e) { return; }
    var ids = Object.keys(prices);
    if (!ids.length) return;
    var qty = {};
    var qtyHost = productScope.querySelector('[data-broq-variant-qty]');
    if (qtyHost) { try { qty = JSON.parse(qtyHost.textContent || '{}'); } catch (e) {} }
    var skus = {};
    var skuHost = productScope.querySelector('[data-broq-variant-skus]');
    if (skuHost) { try { skus = JSON.parse(skuHost.textContent || '{}'); } catch (e) {} }
    // Precio de lista por variante: la pieza y el par tienen descuentos
    // distintos, así que el badge se calcula, nunca se escribe a mano.
    var compares = {};
    var cmpHost = productScope.querySelector('[data-broq-variant-anchors]');
    if (cmpHost) { try { compares = JSON.parse(cmpHost.textContent || '{}'); } catch (e) {} }
    // Config única (T0.1/T1.1, cro/IMPLEMENTACION.md): mientras
    // urgenciaReal sea false, el stock siempre se muestra "resurtible",
    // nunca un número — sin importar el inventario placeholder de Shopify.
    var broqConfig = { umbralEnvio: 1499, umbralMsi: 2999, umbralMsiReal: 2699.1, costoEnvio: 179, umbralExtra: 4999, pctExtra: 10, urgenciaReal: false };
    var cfgHost = productScope.querySelector('[data-broq-config]');
    if (cfgHost) { try { var parsed = JSON.parse(cfgHost.textContent || '{}'); for (var ck in parsed) broqConfig[ck] = parsed[ck]; } catch (e) {} }
    // Con centavos (22-oct-2026): el pill "Por par — $X" tiene que decir lo
    // mismo que el precio principal ($541.79, no $542).
    function fmt(pesos) { return broqMoney(pesos); }
    function currentKey() {
      // El radio marcado del selector "Se vende" da el valor (p. ej. "Por par")
      // de forma SÍNCRONA — a diferencia del [name="id"] que Horizon actualiza tarde.
      var checked = productScope.querySelector('input[data-option-name="Se vende"]:checked')
        || productScope.querySelector('.variant-option input:checked');
      if (checked && prices[checked.value] != null) return checked.value;
      return ids[0];
    }
    function update() {
      var key = currentKey();
      var cents = prices[key];
      if (cents == null) return;
      var pesos = cents / 100;
      // Escrituras GUARDADAS (solo si cambia el valor): update() corre desde
      // un MutationObserver y escribir lo mismo dispararía mutaciones en loop.
      // T0.5: el MSI ya no muestra monto por pieza ("Hasta 3 MSI" es texto
      // estático), así que ya no hay nada que recalcular aquí.

      // El strip de mayoreo ("¿Buscas más descuento?... te saldría en $X")
      // se retiró el 19-oct-2026 (rediseño PDP): la caja "¿Quieres más
      // descuento?" de broq-descuento.liquid no muestra precio por variante.

      // T0.4 (cro/IMPLEMENTACION.md): escalera de precio efectivo — monta
      // los 2 montos sin el bug de espacio del money filter y resalta la
      // fila que corresponde a la variante seleccionada. Los montos NO
      // dependen de `key`: son propiedades del producto (pieza/par están
      // siempre ambos en `prices`), por eso se recalculan siempre igual.
      // La fila del 10% extra se quitó de aquí (Sergio, 30-jul-2026) —
      // ese precio ahora vive en el strip de mayoreo (ver más abajo).
      var esc = productScope.querySelector('[data-broq-escalera]');
      if (esc && prices['Por pieza'] != null && prices['Por par'] != null) {
        var piezaC = prices['Por pieza'];
        var parC = prices['Por par'];
        var parEfectivoC = parC / 2;
        var setEsc = function (attr, val) {
          var el = esc.querySelector('[data-broq-el="' + attr + '"]');
          if (el && el.textContent !== val) el.textContent = val;
        };
        setEsc('pieza', fmt(piezaC / 100));
        setEsc('par', fmt(parC / 100));
        setEsc('par_efectivo', fmt(parEfectivoC / 100));
        esc.querySelectorAll('.broq-escalera__row[data-broq-row]').forEach(function (row) {
          var isActive = row.getAttribute('data-broq-row') === key ? 'true' : 'false';
          if (row.getAttribute('data-broq-active') !== isActive) row.setAttribute('data-broq-active', isActive);
        });
      }

      // CASHBACK (20-ago-2026): el renglón "te regresa $X" de la PDP sigue a
      // la variante seleccionada. INERTE mientras el switch del motor esté
      // apagado: el selector no existe en el DOM y este bloque no corre.
      // La TASA se lee del data-attr que imprime broq-cashback.liquid (fuente
      // única) — aquí no vive ningún porcentaje. Redondeo HACIA ARRIBA al peso,
      // igual que el Liquid y que Redeemly ("Round amounts to the next
      // integer", 22-oct-2026). Aritmética entera: sin ruido de flotante.
      // Desde el 19-oct-2026 hay DOS superficies con el monto en la PDP (la
      // línea del stack y el popup "¿Cómo?"), así que se recorren todas.
      productScope.querySelectorAll('[data-broq-cashback][data-broq-cb-pct]').forEach(function (cbEl) {
        var cbPct = parseInt(cbEl.getAttribute('data-broq-cb-pct'), 10) || 0;
        // Factor de base que imprime el motor Liquid (data-broq-cb-fac). Desde
        // el 19-oct-2026 14:19 (Sergio: "si dices 10%, muestra 10%") es 100 en
        // todas las superficies; se sigue leyendo del attr para que el JS nunca
        // decida por su cuenta cuánto compensar.
        var cbFac = parseInt(cbEl.getAttribute('data-broq-cb-fac'), 10) || 100;
        var cbMonto = cbEl.querySelector('[data-broq-cb-monto]');
        var cbSujeto = cbEl.querySelector('[data-broq-cb-sujeto]');
        var cbSujetoEn = cbEl.querySelector('[data-broq-cb-sujeto-en]');
        if (cbPct > 0 && cbMonto) {
          var cbWant = fmt(Math.floor((cents * cbFac * cbPct + 999999) / 1000000));
          if (cbMonto.textContent !== cbWant) cbMonto.textContent = cbWant;
        }
        if (cbSujeto) {
          var cbSuj = key === 'Por par' ? 'Este par' : 'Esta pieza';
          if (cbSujeto.textContent !== cbSuj) cbSujeto.textContent = cbSuj;
        }
        if (cbSujetoEn) {
          var cbSujEn = key === 'Por par' ? 'este par' : 'esta pieza';
          if (cbSujetoEn.textContent !== cbSujEn) cbSujetoEn.textContent = cbSujEn;
        }
      });

      // T0.3 (cro/IMPLEMENTACION.md): legend "Se vende" -> "Presentación"
      // + precio visible en cada pill ("Por pieza — $249"). No depende de
      // `key`/`isPar`: pinta ambas opciones con su propio precio del mapa.
      productScope.querySelectorAll('fieldset.variant-option').forEach(function (fs) {
        if (!fs.querySelector('input[data-option-name="Se vende"]')) return;
        var legend = fs.querySelector('legend');
        // "Presentación" era lenguaje de catálogo y no le decía nada a la
        // clienta (Sergio, 8-ago). "Pieza o par" dice exactamente qué se elige.
        if (legend && legend.textContent.trim() !== 'Pieza o par') legend.textContent = 'Pieza o par';
        fs.querySelectorAll('.variant-option__button-label').forEach(function (lab) {
          var inp = lab.querySelector('input[data-option-name="Se vende"]');
          var txt = lab.querySelector('.variant-option__button-label__text');
          if (!inp || !txt || prices[inp.value] == null) return;
          var want = fmt(prices[inp.value] / 100);
          var suf = txt.querySelector('.broq-pill-price');
          if (!suf) {
            // lb-cro-heatmap (26-oct-2026): sin separador. La etiqueta va arriba
            // y el precio abajo, en dos renglones (CSS §13). El precio se sigue
            // leyendo del mapa de Shopify ([data-broq-variant-prices]).
            suf = document.createElement('span');
            suf.className = 'broq-pill-price';
            txt.appendChild(suf);
          }
          if (!lab.classList.contains('lb-hm-variante')) lab.classList.add('lb-hm-variante');
          if (suf.textContent !== want) suf.textContent = want;

          // Un solo porcentaje principal; el selector conserva precio y unidad.
          lab.classList.remove('broq-has-off');
          lab.style.removeProperty('--broq-off');
        });
      });
      // Línea de precio (rediseño PDP 19-oct-2026, spec punto 6): "$304 el par"
      // + tag "10% OFF" en lugar del tachado. El "($152 c/u)" que se inyectaba
      // aquí SALIÓ: Sergio lo leía como precio mal calculado frente al $169
      // del selector. El porcentaje se calcula EN VIVO del compare_at de la
      // variante seleccionada (mapa data-broq-variant-compare), redondeado al
      // entero; sin compare_at no se imprime. El tachado lo oculta el CSS
      // (§23 de motion.css). La sticky bar lleva solo la unidad, sin tag.
      var isPar = /par/i.test(key);
      var unidadTxt = isPar ? 'el par' : 'la pieza';
      var listaSel = compares[key];
      var discount = discounts[key] || {};
      var gainLine = productScope.querySelector('[data-broq-ganancia]');
      if (gainLine) {
        if (gainLine.hidden !== !listaSel) gainLine.hidden = !listaSel;
        var subject = gainLine.querySelector('[data-broq-psv-subject]');
        var amount = gainLine.querySelector('[data-broq-psv-amount]');
        var subjectText = isPar ? 'El par lo puedes vender desde' : 'La pieza la puedes vender desde';
        if (subject && subject.textContent !== subjectText) subject.textContent = subjectText;
        if (amount && listaSel && amount.textContent !== fmt(listaSel / 100)) amount.textContent = fmt(listaSel / 100);
      }
      productScope.querySelectorAll('[data-broq-retail-reference]').forEach(function (reference) {
        if (reference.closest('product-card, product-card-link')) return;
        var want = listaSel ? fmt(listaSel / 100) : '';
        if (reference.textContent !== want) reference.textContent = want;
      });
      productScope.querySelectorAll('[data-broq-reference-price]').forEach(function (container) {
        if (container.closest('product-card, product-card-link')) return;
        var comparable = !!listaSel && listaSel > cents && discount.range !== true;
        var regular = container.querySelector('.price__regular');
        var sale = container.querySelector('.price__sale');
        if (regular) regular.classList.toggle('price__hidden', comparable);
        if (sale) sale.classList.toggle('price__hidden', !comparable);
      });
      var discountEligible = discount.available === true && discount.range !== true
        && discount.price === cents && discount.reference === listaSel;
      var offSel = discountEligible && discount.requiresKit !== true ? discount.percent : null;
      var badgeState = {
        percent: discount.percent, variantId: discount.variantId,
        price: cents, reference: listaSel, eligible: discountEligible, requiresKit: discount.requiresKit === true
      };
      productScope.querySelectorAll('[data-broq-gallery-badges] [data-broq-discount-badge]').forEach(function (badge) {
        broqSyncDiscountBadge(badge, badgeState);
      });
      var reviewReason = !listaSel ? 'missing_variant_anchor' : cents >= listaSel ? 'wholesale_at_or_above_anchor' : '';
      if (reviewReason) {
        var reviewKey = key + ':' + reviewReason;
        window.__broqPsvWarnings = window.__broqPsvWarnings || {};
        if (!window.__broqPsvWarnings[reviewKey]) {
          window.__broqPsvWarnings[reviewKey] = true;
          console.warn('broq:psv:review-required', reviewKey);
          document.dispatchEvent(new CustomEvent('broq:psv:review-required', { detail: { variant: key, reason: reviewReason } }));
        }
      }
      // Limpieza: cualquier "c/u" heredado de la versión anterior del script.
      productScope.querySelectorAll('.broq-cu').forEach(function (cu) { cu.remove(); });
      // WHITELIST: solo el precio del producto principal y la sticky bar.
      var priceEls = productScope.querySelectorAll('product-price [data-broq-reference-price] span.price, .sticky-add-to-cart__price span.price');
      priceEls.forEach(function (pe) {
        if (pe.closest('product-card, product-card-link')) return;
        if (!/\$/.test(pe.textContent || '')) return;
        var enSticky = !!pe.closest('.sticky-add-to-cart__price');
        var un = pe.querySelector('.broq-precio-unidad');
        if (!un) { un = document.createElement('span'); un.className = 'broq-precio-unidad'; pe.appendChild(un); }
        var wantUn = ' ' + unidadTxt;
        if (un.textContent !== wantUn) un.textContent = wantUn;
        var tag = pe.querySelector('.broq-offtag');
        if (!enSticky && offSel && offSel > 0) {
          if (!tag) { tag = document.createElement('span'); tag.className = 'broq-offtag'; pe.appendChild(tag); }
          var wantTag = '-' + offSel + '% OFF';
          tag.title = 'Comparado con el precio sugerido de venta en menudeo';
          if (tag.textContent !== wantTag) tag.textContent = wantTag;
        } else if (tag) { tag.remove(); }
      });
      var pu = productScope.querySelector('[data-broq-porunidad]');
      if (pu) { pu.hidden = true; }
      // T0.1: stock honesto con número real, SOLO si urgenciaReal=true y existe
      // el renglón [data-broq-stock] (desde el 19-oct-2026 la PDP no lo pinta:
      // "Stock limitado" es texto fijo por decisión de Sergio). Se conserva
      // para no reventar si otra plantilla lo monta.
      var st = productScope.querySelector('[data-broq-stock]');
      if (st && qty[key] != null && st.getAttribute('data-broq-q') !== String(qty[key])) {
        var q = qty[key];
        st.setAttribute('data-broq-q', String(q));
        if (broqConfig.urgenciaReal && q > 0 && q < 20) {
          st.innerHTML = '<span class="broq-stock__dot broq-stock__dot--urg"></span><span class="broq-stock__urg">Quedan <strong>' + q + '</strong> piezas de esta presentación.</span>';
        } else {
          st.innerHTML = '<span class="broq-stock__dot"></span><span class="broq-stock__ok">En stock — producto de línea, <strong>resurtible</strong>.</span>';
        }
      }
      // T0.6: SKU en la ficha técnica — difiere por variante (BRQ-1003 vs
      // BRQ-1003-PAR), así que se actualiza igual que stock/precio.
      var skuEl = productScope.querySelector('[data-broq-sku]');
      if (skuEl && skus[key] && skuEl.textContent !== skus[key]) skuEl.textContent = skus[key];
      // La ETA cuenta desde el DÍA EN QUE SALE el pedido, no desde hoy: ver
      // broqPintarEta(). Es idempotente, así que se puede llamar en cada update.
      broqPintarEta();
    }

    // Re-ejecuta el update varias veces: el morph del variant-picker de
    // Horizon re-renderiza el selector tras el cambio y podría pisar el
    // resultado, así que reaplicamos cuando ya asentó (0/150/400/800ms).
    function updateSoon() {
      update();
      requestAnimationFrame(update);
      setTimeout(update, 150);
      setTimeout(update, 400);
      setTimeout(update, 800);
    }

    update();
    if (host.getAttribute('data-broq-bound') === 'true') return;
    host.setAttribute('data-broq-bound', 'true');
    // Horizon MORPHEA product-price tras el cambio de variante (fetch de
    // sección) y borra el "(… c/u)"; los reintentos con timeout no bastan.
    // Un observer re-aplica al asentarse el DOM; update() es idempotente
    // (escrituras guardadas), así que no cicla.
    if (!window.__broqCuObs) {
      var cuTimer = null;
      window.__broqCuObs = new MutationObserver(function () {
        clearTimeout(cuTimer);
        cuTimer = setTimeout(update, 80);
      });
      window.__broqCuObs.observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener('shopify:product:select', updateSoon);
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t) return;
      if ((t.name && /^Se vende/i.test(t.name)) || (t.closest && t.closest('variant-picker, .variant-picker'))) {
        updateSoon();
      }
    });
  }

  /**
   * T1.2 (cro/IMPLEMENTACION.md) — Quick-add sin modal en tarjetas de
   * colección: dos botones por tarjeta ("+ Pieza $X" / "+ Par $Y") que
   * agregan directo al carrito y se convierten en stepper (− n +).
   *
   * NO reutiliza <product-form-component> (riesgo confirmado: ese
   * componente resincroniza su variant id oculto con cualquier
   * ProductSelectEvent que burbujee desde la MISMA tarjeta — p.ej. el
   * bloque de swatches — y pisaría nuestro variant id fijo). En vez de
   * eso hace fetch directo a /cart/add.js y /cart/change.js, y despacha
   * un CustomEvent "shopify:cart:lines-update" construido a mano —
   * mismo tipo y misma forma ({promise} que resuelve a {cart, detail})
   * que la clase real CartLinesUpdateEvent de '@shopify/events', pero
   * SIN importar ese módulo: `import()` dinámico dentro de este script
   * clásico (sin type="module") revienta en el CDN de Shopify con
   * "ReferenceError: require is not defined" (confirmado en vivo — el
   * pipeline de assets reescribe el import() asumiendo un bundler). Los
   * consumidores (cart-icon.js, component-cart-items.js) solo leen
   * `event.promise` y el `{cart, detail}` con el que se resuelve — no
   * verifican que la instancia venga de esa clase — así que un
   * CustomEvent liso con esas mismas propiedades es indistinguible
   * para ellos, y evita la dependencia del import map por completo:
   *   - El bubble de cantidad del header (cart-icon.js) lo escucha y
   *     se actualiza con cart.totalQuantity del evento resuelto.
   *   - El Cart Drawer (component-cart-items.js #handleCartUpdate) lo
   *     escucha y se automorfea EN SEGUNDO PLANO con el HTML de
   *     `sections['cart-drawer-section']` que mandamos en el detail —
   *     sin que nosotros lo abramos ni toquemos su morph directamente.
   *     Si no mandamos sections, el propio componente hace un
   *     re-fetch de respaldo (sectionRenderer.renderSection), así que
   *     esto no puede quedar "roto", solo menos eficiente.
   *
   * Los botones son <button> reales (no <a>): product-card.js ignora
   * clicks en button/input/label/select al decidir si navega a la PDP,
   * así que no compiten con el link de la tarjeta.
   *
   * Delegación de eventos en `document` (no listeners por botón): las
   * tarjetas se re-crean en cada morph de filtro/paginación infinita de
   * Horizon (sectionRenderer + morph.js), así que cualquier listener
   * atado a un botón específico moriría con él. Un MutationObserver
   * (mismo patrón que window.__broqCuObs) sincroniza el estado
   * add-vs-stepper de las tarjetas que van apareciendo.
   */
  function initQuickAdd() {
    if (window.__broqQaInit) return;
    window.__broqQaInit = true;

    var cartItemsByVariant = {}; // variantId(String) -> quantity
    var cartLineKeyByVariant = {}; // variantId(String) -> line key ("variantId:hash"), para /cart/change.js
    var cartLoaded = false;
    var CART_LINES_UPDATE = 'shopify:cart:lines-update'; // StandardEvents.cartLinesUpdate (@shopify/events), confirmado en vivo
    var cancelPendingDrawerOpen = null;

    function fmtQa(pesos) {
      try { return '$' + Math.round(pesos).toLocaleString('es-MX'); }
      catch (e) { return '$' + Math.round(pesos); }
    }

    function refreshCartCache() {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) {
          if (!r.ok) throw new Error('quickadd: cart read failed');
          return r.json();
        })
        .then(function (cart) {
          if (!cart || !Array.isArray(cart.items)) throw new Error('quickadd: invalid cart response');
          var map = {};
          var keyMap = {};
          (cart.items || []).forEach(function (it) {
            map[String(it.variant_id)] = (map[String(it.variant_id)] || 0) + it.quantity;
            // /cart/change.js NO acepta el variant_id plano como "id" — exige
            // la key real de la línea ("variantId:hash de properties"),
            // confirmado en vivo (400 "Identificación o parámetro lineal no
            // válidos" al mandar solo el variant_id). /cart/add.js sí acepta
            // el variant_id plano, por eso el add inicial no necesita esto.
            keyMap[String(it.variant_id)] = it.key;
          });
          cartItemsByVariant = map;
          cartLineKeyByVariant = keyMap;
          cartLoaded = true;
          // A fresh cart can release a control previously locked after an
          // uncertain mutation. Never release it from a stale DOM sync.
          document.querySelectorAll('[data-broq-qa-uncertain]').forEach(function (control) {
            control.removeAttribute('data-broq-qa-uncertain');
            control.removeAttribute('data-broq-qa-busy');
            var button = control.querySelector('[data-broq-qa-add]');
            if (button) button.disabled = false;
            var notice = control.querySelector('[data-broq-qa-error]');
            if (notice) notice.remove();
            setControlState(control, map[control.getAttribute('data-variant-id')] || 0);
          });
          return cart;
        });
    }

    function setControlState(control, qty) {
      var addBtn = control.querySelector('[data-broq-qa-add]');
      var stepper = control.querySelector('[data-broq-qa-stepper]');
      var qtyEl = control.querySelector('[data-broq-qa-qty]');
      qty = qty || 0;
      if (qty > 0) {
        if (addBtn) addBtn.hidden = true;
        if (stepper) stepper.hidden = false;
        if (qtyEl && qtyEl.textContent !== String(qty)) qtyEl.textContent = String(qty);
      } else {
        if (addBtn) addBtn.hidden = false;
        if (stepper) stepper.hidden = true;
      }
    }

    function syncControls(root) {
      var scope = root && root.querySelectorAll ? root : document;
      var controls = scope.querySelectorAll('[data-broq-qa-control]');
      controls.forEach(function (control) {
        var vid = control.getAttribute('data-variant-id');
        if (!vid) return;
        setControlState(control, cartItemsByVariant[vid] || 0);
      });
    }

    function dispatchCartEvent(action, variantId, quantity, cartPromise) {
      // `promise` se resuelve más abajo; se pasa ya en el evento porque
      // los consumidores nativos (cart-icon.js, component-cart-items.js)
      // leen event.promise de forma síncrona justo al recibir el evento.
      var resolvePromise, rejectPromise;
      var promise = new Promise(function (resolve, reject) { resolvePromise = resolve; rejectPromise = reject; });
      var event = new CustomEvent(CART_LINES_UPDATE, { bubbles: true, composed: true });
      event.promise = promise;
      event.action = action;
      event.context = 'product';
      event.lines = [{ merchandiseId: String(variantId), quantity: quantity }];
      event.__broqSource = 'broq-quickadd';
      document.dispatchEvent(event);
      cartPromise.then(function (result) {
        resolvePromise({
          cart: { totalQuantity: result.cart.item_count, items: result.cart.items, currency: result.cart.currency },
          detail: {
            items: result.cart.items,
            sections: result.sections || undefined,
            source: 'broq-quickadd',
            didError: false,
          },
        });
      }).catch(function (err) { rejectPromise(err); });
    }

    function flyToCart(control) {
      var host = control.closest('[data-broq-quickadd]');
      var image = host ? host.getAttribute('data-broq-qa-image') : '';
      var cartIcon = document.querySelector('.header-actions__cart-icon');
      var source = control.querySelector('[data-broq-qa-add]') || control;
      if (!image || !cartIcon || !source || !window.customElements || !customElements.get('fly-to-cart')) return;
      var fly = document.createElement('fly-to-cart');
      fly.classList.add('fly-to-cart--quick');
      fly.style.setProperty('background-image', 'url(' + image + ')');
      fly.style.setProperty('--start-opacity', '0');
      fly.source = source;
      fly.destination = cartIcon;
      document.body.appendChild(fly);
    }

    function postJson(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (r) {
        return r.json().then(function (json) { return { ok: r.ok, json: json }; });
      });
    }

    function openConfirmedCollectionCart(control, cart) {
      if (cancelPendingDrawerOpen) cancelPendingDrawerOpen();
      if (!control.isConnected || !control.closest('collection-component')) return;
      var drawer = document.getElementById('cart-drawer');
      if (!drawer || drawer.hasAttribute('open') || !cart.items.length || !window.customElements) return;
      var expected = cart.items.map(function (item) { return { key: item.key, quantity: item.quantity }; });
      if (expected.some(function (item) { return !item.key; })) return;
      var done = false;
      var observer;
      var timer;

      function cleanup() {
        done = true;
        if (observer) observer.disconnect();
        if (timer) clearTimeout(timer);
        if (cancelPendingDrawerOpen === cleanup) cancelPendingDrawerOpen = null;
      }

      function check() {
        if (done) return;
        if (!drawer.isConnected || !control.isConnected || drawer.hasAttribute('open')) { cleanup(); return; }
        if (typeof drawer.open !== 'function') return;
        var rows = Array.prototype.slice.call(drawer.querySelectorAll('tr.cart-items__table-row[data-key]'));
        if (rows.length !== expected.length) return;
        var ready = expected.every(function (item) {
          var row = rows.find(function (candidate) { return candidate.getAttribute('data-key') === item.key; });
          var quantity = row && row.querySelector('input[name="updates[]"]');
          return quantity && Number(quantity.value) === item.quantity;
        });
        if (!ready) return;
        cleanup();
        // The add button is now hidden. Give the native drawer a visible
        // control to restore focus to when the customer closes it.
        var returnFocus = control.querySelector('[data-broq-qa-plus]');
        if (returnFocus && !returnFocus.disabled) returnFocus.focus({ preventScroll: true });
        drawer.open();
      }

      cancelPendingDrawerOpen = cleanup;
      observer = new MutationObserver(check);
      observer.observe(drawer, { childList: true, subtree: true, attributes: true, attributeFilter: ['value', 'open'] });
      timer = setTimeout(cleanup, 10000);
      customElements.whenDefined('theme-drawer').then(check);
      check();
    }

    function afterMutation(control, variantId, action, addResponseSections) {
      return refreshCartCache().then(function (cart) {
        setControlState(control, cartItemsByVariant[String(variantId)] || 0);
        dispatchCartEvent(action, variantId, cartItemsByVariant[String(variantId)] || 0, Promise.resolve({ cart: cart, sections: addResponseSections }));
        if (action === 'add') openConfirmedCollectionCart(control, cart);
      });
    }

    // Local diagnostics only: no identifiers, customer data or network calls.
    // Confirmed means the mutation AND the rendered cart cache have resolved.
    function qaNotice(control, message, linkToCart) {
      var notice = control.querySelector('[data-broq-qa-error]');
      if (!notice) {
        notice = document.createElement('p');
        notice.className = 'broq-quickadd__error';
        notice.setAttribute('data-broq-qa-error', '');
        notice.setAttribute('role', 'alert');
        control.appendChild(notice);
      }
      notice.textContent = message;
      if (linkToCart) {
        var link = document.createElement('a');
        link.href = '/cart'; link.textContent = 'Ver pedido';
        notice.appendChild(document.createTextNode(' ')); notice.appendChild(link);
      }
    }

    function qaOperation(control, action, run) {
      if (control.getAttribute('data-broq-qa-busy') === 'true') return;
      var startedAt = performance.now();
      var addBtn = control.querySelector('[data-broq-qa-add]');
      var state = { accepted: false, rejected: false, sections: null };
      var oldNotice = control.querySelector('[data-broq-qa-error]');
      if (oldNotice) oldNotice.remove();
      function report(phase) {
        document.dispatchEvent(new CustomEvent('broq:cart-operation', {
          detail: { source: 'quickadd', action: action, phase: phase, durationMs: Math.round(performance.now() - startedAt) }
        }));
      }
      function unlock() {
        control.removeAttribute('data-broq-qa-busy');
        if (addBtn) addBtn.disabled = false;
      }
      control.setAttribute('data-broq-qa-busy', 'true');
      if (addBtn) addBtn.disabled = true;
      report('start');
      return Promise.resolve().then(function () { return run(state); })
        .then(function () { report('confirmed'); unlock(); }, function () {
          if (state.rejected) {
            qaNotice(control, 'No pudimos actualizar tu pedido. Intenta de nuevo.', false);
            report('error'); unlock(); return;
          }
          // The POST may already have reached Shopify. Reconcile once with
          // GET only; never retry an add/change mutation automatically.
          return refreshCartCache().then(function (cart) {
            var variantId = control.getAttribute('data-variant-id');
            var qty = cartItemsByVariant[String(variantId)] || 0;
            setControlState(control, qty);
            dispatchCartEvent(action, variantId, qty, Promise.resolve({ cart: cart, sections: state.sections }));
            if (state.accepted && action === 'add') openConfirmedCollectionCart(control, cart);
            if (!state.accepted) qaNotice(control, 'Revisa la cantidad en tu pedido.', true);
            report(state.accepted ? 'confirmed' : 'error'); unlock();
          }, function () {
            control.setAttribute('data-broq-qa-uncertain', 'true');
            qaNotice(control, 'No pudimos confirmar el cambio.', true);
            report('error');
            // Keep this control locked until another successful cart read or
            // navigation. A retry here could silently add the same item twice.
          });
        });
    }

    function handleAdd(control) {
      var variantId = control.getAttribute('data-variant-id');
      if (!variantId) return;
      return qaOperation(control, 'add', function (state) {
        return postJson('/cart/add.js', { id: Number(variantId), quantity: 1, sections: 'cart-drawer-section' })
          .then(function (res) {
            if (!res.ok) { state.rejected = true; throw new Error('quickadd: add failed'); }
            state.accepted = true; state.sections = res.json.sections;
            flyToCart(control);
            return afterMutation(control, variantId, 'add', res.json.sections);
          });
      });
    }

    function handleStep(control, delta) {
      var variantId = control.getAttribute('data-variant-id');
      if (!variantId) return;
      var lineKey = cartLineKeyByVariant[String(variantId)];
      if (!lineKey) return;
      var current = cartItemsByVariant[String(variantId)] || 0;
      var next = Math.max(0, current + delta);
      if (next === current) return;
      var action = next === 0 ? 'remove' : 'update';
      return qaOperation(control, action, function (state) {
        return postJson('/cart/change.js', { id: lineKey, quantity: next, sections: 'cart-drawer-section' })
          .then(function (res) {
            if (!res.ok) { state.rejected = true; throw new Error('quickadd: change failed'); }
            state.accepted = true; state.sections = res.json.sections;
            return afterMutation(control, variantId, action, res.json.sections);
          });
      });
    }

    document.addEventListener('click', function (e) {
      var addEl = e.target.closest && e.target.closest('[data-broq-qa-add]');
      if (addEl) {
        var control = addEl.closest('[data-broq-qa-control]');
        if (control) handleAdd(control);
        return;
      }
      var plusEl = e.target.closest && e.target.closest('[data-broq-qa-plus]');
      if (plusEl) {
        var pControl = plusEl.closest('[data-broq-qa-control]');
        if (pControl) handleStep(pControl, 1);
        return;
      }
      var minusEl = e.target.closest && e.target.closest('[data-broq-qa-minus]');
      if (minusEl) {
        var mControl = minusEl.closest('[data-broq-qa-control]');
        if (mControl) handleStep(mControl, -1);
      }
    });

    // Estado inicial + resync tras cada morph de grid (filtros/paginación)
    // y tras cualquier cambio de carrito disparado desde OTRO lado del
    // tema (PDP, Cart Drawer) para que las tarjetas visibles no queden
    // desfasadas si el mismo producto se tocó en otra parte.
    refreshCartCache().then(function () { syncControls(document); });

    if (!window.__broqQaObs) {
      var qaTimer = null;
      window.__broqQaObs = new MutationObserver(function () {
        clearTimeout(qaTimer);
        qaTimer = setTimeout(function () { syncControls(document); }, 120);
      });
      window.__broqQaObs.observe(document.body, { childList: true, subtree: true });
    }

    document.addEventListener(CART_LINES_UPDATE, function (event) {
      if (event && event.__broqSource === 'broq-quickadd') return; // ya sincronizado en afterMutation
      if (event && event.promise && typeof event.promise.then === 'function') {
        event.promise.then(function () { refreshCartCache().then(function () { syncControls(document); }); }).catch(function () {});
      }
    });
  }

  /**
   * Helpers compartidos por initCartSwap y initCartProgress (T1.3c/T1.3a).
   * initQuickAdd (arriba) ya probado en vivo con su propia copia interna
   * de estos mismos helpers — no se toca para no arriesgar una regresión
   * en código ya verificado; los dos nuevos consumidores sí comparten
   * uno solo, siguiendo la regla de 3 repeticiones.
   */
  var BroqCart = {
    CART_LINES_UPDATE: 'shopify:cart:lines-update',
    fmt: function (pesos) {
      try { return '$' + Math.round(pesos).toLocaleString('es-MX'); }
      catch (e) { return '$' + Math.round(pesos); }
    },
    postJson: function (url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      }).then(function (r) {
        return r.json().then(function (json) { return { ok: r.ok, json: json }; });
      });
    },
    getCart: function () {
      return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
    },
    // ¿Shopify aplicó el escalón de súper mayoreo? Se detecta POR REGLA, no por
    // porcentaje (POPUP-10, CRO 22-oct-2026): el automático con ese título está en
    // cart_level_discount_applications de /cart.js. Antes era "descuento > 7% del
    // subtotal", y el LB10- (10%) de bienvenida dejaba la barra en blanco (E4) y
    // sin aviso de envío gratis. El umbral sigue sin estar en el código (19-oct).
    // El título se puede cambiar en data-broq-config (tierAltoTitulo); si alguien
    // renombra el automático sin cambiarlo, la barra cae a 5%/E3, nunca a E4 vacío.
    TIER_ALTO_TITULO: 'Descuento de mayoreo',
    tierAltoTitulo: function () {
      try {
        var cfg = document.querySelector('[data-broq-config]');
        var t = cfg && JSON.parse(cfg.textContent || '{}').tierAltoTitulo;
        if (typeof t === 'string' && t) return t;
      } catch (e) {}
      return BroqCart.TIER_ALTO_TITULO;
    },
    descuentoSobrePctExtra: function (cart, pctExtra) {
      var apps = (cart && cart.cart_level_discount_applications) || [];
      var titulo = BroqCart.tierAltoTitulo();
      for (var i = 0; i < apps.length; i++) {
        var a = apps[i];
        if (a && a.type === 'automatic' && a.title === titulo && Number(a.total_allocated_amount) > 0) return true;
      }
      return false;
    },
    // Código del pop-up de bienvenida (LB5-/LB10-) que Shopify aplicó: { code, pct } o null.
    // Si el escalón alto le gana, Shopify no lo aplica y aquí da null.
    // OFERTA NUEVA (CRO §1): 10% extra = automático de PRODUCTO "10% extra por surtido"; vive en
    // items[].line_level_discount_allocations. Título en data-broq-config (extraTitulo).
    EXTRA_TITULO: '10% extra por surtido',
    cfgVivo: function () {
      try { var el = document.querySelector('[data-broq-config]'); return el ? JSON.parse(el.textContent || '{}') : {}; } catch (e) { return {}; }
    },
    tieneExtra: function (cart) {
      var t = BroqCart.cfgVivo().extraTitulo || BroqCart.EXTRA_TITULO;
      var items = (cart && cart.items) || [];
      for (var j = 0; j < items.length; j++) {
        var al = items[j].line_level_discount_allocations || [];
        for (var k = 0; k < al.length; k++) {
          var da = al[k] && al[k].discount_application;
          if (da && da.title === t && Number(al[k].amount) > 0) return true;
        }
      }
      return false;
    },
    // Productos a precio publicado, ANTES de todo descuento (base del 10% extra).
    baseExtra: function (cart) {
      var c = 0, items = (cart && cart.items) || [];
      for (var i = 0; i < items.length; i++) c += Number(items[i].original_line_price) || 0;
      return c / 100;
    },
    // LB5- capturado pero no aplicado. /cart.js trae discount_codes [{code, applicable}];
    // si no viene, usa lo que pintó Liquid al cargar (data-broq-lb5-fuera).
    lb5Fuera: function (cart, host) {
      var dcs = cart && cart.discount_codes;
      if (Array.isArray(dcs)) {
        for (var i = 0; i < dcs.length; i++) {
          if (dcs[i] && /^LB5-/i.test(dcs[i].code || '') && dcs[i].applicable === false) return true;
        }
        return false;
      }
      return !!(host && host.getAttribute('data-broq-lb5-fuera') === '1');
    },
    bienvenida: function (cart) {
      var apps = (cart && cart.cart_level_discount_applications) || [];
      for (var i = 0; i < apps.length; i++) {
        var a = apps[i];
        var m = a && a.type === 'discount_code' && /^LB(5|10)-/i.exec(a.title || '');
        if (m && Number(a.total_allocated_amount) > 0) return { code: a.title, pct: Number(m[1]) };
      }
      return null;
    },
    dispatchCartEvent: function (action, variantId, quantity, cartPromise, source) {
      var resolvePromise, rejectPromise;
      var promise = new Promise(function (resolve, reject) { resolvePromise = resolve; rejectPromise = reject; });
      var event = new CustomEvent(BroqCart.CART_LINES_UPDATE, { bubbles: true, composed: true });
      event.promise = promise;
      event.action = action;
      event.context = 'product';
      event.lines = [{ merchandiseId: String(variantId), quantity: quantity }];
      event.__broqSource = source || 'broq-cart';
      document.dispatchEvent(event);
      cartPromise.then(function (cart) {
        resolvePromise({
          cart: { totalQuantity: cart.item_count, items: cart.items, currency: cart.currency },
          detail: { items: cart.items, source: source || 'broq-cart', didError: false },
        });
      }).catch(function (err) { rejectPromise(err); });
    },
  };

  /**
   * T1.3c (cro/IMPLEMENTACION.md) — "Cámbiala a par y ahorra $XX" por
   * línea del Cart Drawer. Un tap: /cart/change.js pone la línea pieza
   * en 0 y /cart/add.js agrega la variante par con la misma cantidad.
   * Ambas llamadas van SECUENCIALES (no en paralelo) — si se agrega antes
   * de quitar, por un instante hay dos líneas del mismo producto y el
   * usuario ve un parpadeo raro en el drawer.
   */
  function initCartSwap() {
    if (window.__broqSwapInit) return;
    window.__broqSwapInit = true;

    function showToast(text) {
      var toast = document.createElement('div');
      toast.className = 'broq-swap-toast';
      toast.textContent = text;
      document.body.appendChild(toast);
      requestAnimationFrame(function () { toast.classList.add('is-visible'); });
      setTimeout(function () {
        toast.classList.remove('is-visible');
        setTimeout(function () { toast.remove(); }, 300);
      }, 2600);
    }

    document.addEventListener('click', function (e) {
      var btn = e.target.closest && e.target.closest('[data-broq-swap-btn]');
      if (!btn) return;
      var wrap = btn.closest('[data-broq-swap]');
      if (!wrap || wrap.getAttribute('data-broq-swap-busy') === 'true') return;
      var lineKey = wrap.getAttribute('data-line-key');
      var parId = wrap.getAttribute('data-par-variant-id');
      var qty = Number(wrap.getAttribute('data-qty')) || 1;
      var ahorro = wrap.getAttribute('data-ahorro') || '';
      wrap.setAttribute('data-broq-swap-busy', 'true');
      btn.disabled = true;

      // /cart/change.js exige la KEY real de la línea ("variantId:hash"),
      // NO el variant id plano (mismo bug ya resuelto en initQuickAdd's
      // handleStep — confirmado en vivo: 400 "Identificación o parámetro
      // lineal no válidos" si se manda solo el variant id). item.key de
      // Liquid ya nos la da directo, sin pasos extra.
      BroqCart.postJson('/cart/change.js', { id: lineKey, quantity: 0, sections: 'cart-drawer-section' })
        .then(function (removeRes) {
          if (!removeRes.ok) throw new Error('swap: no se pudo quitar la pieza');
          return BroqCart.postJson('/cart/add.js', { id: Number(parId), quantity: qty, sections: 'cart-drawer-section' });
        })
        .then(function (addRes) {
          if (!addRes.ok) throw new Error('swap: no se pudo agregar el par');
          return BroqCart.getCart().then(function (cart) {
            BroqCart.dispatchCartEvent('update', parId, qty, Promise.resolve(cart), 'broq-cart-swap');
            showToast('Listo: par agregado, ahorraste ' + ahorro + ' ✓');
          });
        })
        .catch(function () {
          wrap.removeAttribute('data-broq-swap-busy');
          btn.disabled = false;
        });
    });
  }

  /**
   * T1.3a (cro/IMPLEMENTACION.md) — barra multi-hito del Cart Drawer.
   * Liquid ya pintó el estado inicial correcto (broq-cart-progress.liquid);
   * este JS la mantiene viva escuchando el mismo evento de carrito que
   * usan broq-quickadd y el swap, sin importar cuál de los 3 caminos de
   * render del drawer disparó el cambio (SSR, Section Rendering, o el
   * swap cliente-only a <template id="empty-cart-template">).
   */
  function initCartProgress() {
    if (window.__broqCpInit) return;
    window.__broqCpInit = true;

    var UMBRAL_ENVIO = 1499;
    var UMBRAL_EXTRA = 4999;
    var COSTO_ENVIO = 179;
    var PCT_EXTRA = 10;
    var cfgV = BroqCart.cfgVivo();
    var UMBRAL_MSI = Number(cfgV.umbralMsiReal) || 2699.1;      // real, sobre lo pagado
    var UMBRAL_MSI_ANUN = Number(cfgV.umbralMsi) || 2999;     // anunciado

    // OFERTA NUEVA (CRO SITIO-FINAL §1). Gemelo de broq-cart-progress.liquid.
    // baseEnvio = total_price (envío y MSI) · baseExtra = productos antes de descuentos ·
    // tieneExtra = Shopify ya aplicó el 10% extra. E3x = falla (≥ $4,999 sin 10%): no promete.
    function computeState(baseExtra, baseEnvio, itemCount, tieneExtra) {
      if (itemCount === 0) return 'E0';
      if (tieneExtra) return 'E4';
      if (baseEnvio < UMBRAL_ENVIO) return (UMBRAL_ENVIO - baseEnvio) <= 400 ? 'E1b' : 'E1';
      if (baseEnvio < UMBRAL_MSI) return 'E2';
      var gap = UMBRAL_EXTRA - baseExtra;
      if (gap <= 0) return 'E3x';
      return gap <= 1200 ? 'E3b' : 'E3';
    }

    function render(cart) {
      document.querySelectorAll('[data-broq-cart-progress]').forEach(function (host) {
      var baseExtra = BroqCart.baseExtra(cart);
      var baseEnvio = (cart.total_price != null ? cart.total_price : cart.items_subtotal_price) / 100;
      var tieneExtra = BroqCart.tieneExtra(cart);
      var state = computeState(baseExtra, baseEnvio, cart.item_count, tieneExtra);
      if (state === 'E3x' && window.console) console.warn('[broq] productos ≥ $4,999 sin el 10% extra aplicado');
      var bv = BroqCart.bienvenida(cart);
      var gapEnvio = Math.max(0, Math.ceil(UMBRAL_ENVIO - baseEnvio));
      var gapMsi = Math.max(0, Math.ceil(UMBRAL_MSI_ANUN - baseExtra));   // Copy 18:40: 2999 − productos antes de descuentos
      var gapExtra = Math.max(0, Math.ceil(UMBRAL_EXTRA - baseExtra));
      var prevState = host.getAttribute('data-broq-state');

      var titulo = host.querySelector('[data-broq-cp-titulo]');
      var sub = host.querySelector('[data-broq-cp-sub]');
      var fill = host.querySelector('[data-broq-cp-fill]');
      var marker = host.querySelector('[data-broq-cp-marker]');
      var markerMsi = host.querySelector('[data-broq-cp-marker-msi]');
      var markerEnd = host.querySelector('[data-broq-cp-marker-end]');

      var titulos = {
        E0: 'Surte con oro de verdad.',
        E1: 'Te faltan <strong>' + BroqCart.fmt(gapEnvio) + '</strong> para tu envío GRATIS 🚚',
        E1b: '¡Solo <strong>' + BroqCart.fmt(gapEnvio) + '</strong> y tu envío sale GRATIS!',
        E2: 'Tu envío ya va gratis ✓',
        E3: 'Ya puedes pagar hasta en 3 meses sin intereses con tarjetas participantes.',
        E3x: 'Ya puedes pagar hasta en 3 meses sin intereses con tarjetas participantes.',
        E3b: 'Estás a <strong>' + BroqCart.fmt(gapExtra) + '</strong> del 10% extra en todo tu pedido',
        E4: bv ? '10% extra aplicado ✓ Tu código de primer pedido ✓' : '10% extra aplicado ✓ Envío gratis ✓'
      };
      var subs = {
        E0: 'Envío gratis desde ' + BroqCart.fmt(UMBRAL_ENVIO) + ' · Meses sin intereses desde ' + BroqCart.fmt(UMBRAL_MSI_ANUN) + ' · 10% extra en compras +' + BroqCart.fmt(UMBRAL_EXTRA),
        E1: 'Hoy tu envío costaría $' + COSTO_ENVIO + '. Mejor conviértelo en ganancia.',
        E1b: 'Con el siguiente par ya lo tienes ✓',
        E2: gapMsi > 0 ? 'Te faltan <strong>' + BroqCart.fmt(gapMsi) + '</strong> para pagar hasta en 3 meses sin intereses.' : '',
        E3: 'Te faltan <strong>' + BroqCart.fmt(gapExtra) + '</strong> en productos para el 10% extra en todo tu pedido.',
        E3x: '',
        E3b: '',
        E4: baseEnvio >= UMBRAL_MSI ? 'Y puedes pagarlo hasta en 3 meses sin intereses.' : ''
      };
      // Copy versión A: nunca un % sumado; cada descuento por su nombre.
      if (bv && state === 'E4') subs.E4 = 'Ves los dos descuentos abajo, cada uno en su renglón.';
      else if (bv && state !== 'E0') subs[state] = (subs[state] || '') + '<span class="broq-cart-progress__codigo" style="display:block">Tu código de primer pedido ya está puesto ✓</span>';
      // CRO §1 F8-no / QA #19: LB5- capturado y no aplicado con el 10% extra (el 10% siempre es el mayor).
      if (!bv && state === 'E4' && BroqCart.lb5Fuera(cart, host)) subs.E4 = 'Tu código no se suma con el 10% extra. Ya te dejamos el que más te conviene.';

      host.hidden = false;
      if (titulo && titulo.innerHTML !== titulos[state]) titulo.innerHTML = titulos[state];
      if (sub && sub.innerHTML !== subs[state]) sub.innerHTML = subs[state];

      if (fill) {
        var fillPct = Math.min(100, (baseExtra * 100) / UMBRAL_EXTRA);
        fill.style.width = fillPct + '%';
        if (state !== prevState && (state === 'E2' || state === 'E3' || state === 'E4') && prevState !== null) {
          fill.classList.remove('is-celebrating');
          void fill.offsetWidth;
          fill.classList.add('is-celebrating');
          setTimeout(function () { fill.classList.remove('is-celebrating'); }, 700);
        }
      }
      if (marker) marker.classList.toggle('is-done', baseEnvio >= UMBRAL_ENVIO);
      if (markerMsi) markerMsi.classList.toggle('is-done', baseEnvio >= UMBRAL_MSI);
      if (markerEnd) markerEnd.classList.toggle('is-done', tieneExtra);

      host.setAttribute('data-broq-state', state);
      });
    }

    document.addEventListener(BroqCart.CART_LINES_UPDATE, function (event) {
      if (event && event.promise && typeof event.promise.then === 'function') {
        event.promise.then(function (result) {
          BroqCart.getCart().then(render);
        }).catch(function () {});
      } else {
        BroqCart.getCart().then(render);
      }
    });

    // QA #12/#19 (20:46): el objeto cart de Liquid NO expone códigos capturados pero no
    // aplicables (solo /cart.js trae discount_codes[].applicable), así que el SSR de un E4
    // sin código aplicado no sabe si hay un LB5- fuera y pinta el texto de MSI. Al cargar
    // (recarga, /cart) se repinta UNA vez con /cart.js para mostrar el aviso de LB5 fuera.
    var ssrE4SinCodigo = false;
    document.querySelectorAll('[data-broq-cart-progress]').forEach(function (h) {
      if (h.getAttribute('data-broq-state') === 'E4') ssrE4SinCodigo = true;
    });
    if (ssrE4SinCodigo) BroqCart.getCart().then(render).catch(function () {});
  }

  /**
   * T1.6 (cro/IMPLEMENTACION.md) — barra sticky de pedido en colección.
   * Versión compacta de initCartProgress: mismo evento, mismo helper.
   * Liquid ya pintó el estado inicial correcto; el JS solo la mantiene
   * viva y la oculta cuando el carrito llega a 0.
   */
  function broqCartUnits(cart) {
    var pairs = 0, pieces = 0, unknown = false;
    (cart.items || []).forEach(function (item) {
      var title = String(item.variant_title || (item.variant && item.variant.title) || '').trim().toLowerCase();
      var qty = Number(item.quantity);
      if (!(qty > 0)) { unknown = true; return; }
      if (title === 'por par') pairs += qty;
      else if (title === 'por pieza') pieces += qty;
      else unknown = true;
    });
    if (unknown || (!pairs && !pieces)) return 'Ver pedido';
    var units = [];
    if (pairs) units.push(pairs + (pairs === 1 ? ' par' : ' pares'));
    if (pieces) units.push(pieces + (pieces === 1 ? ' pieza' : ' piezas'));
    return units.join(' + ');
  }

  function initCollectionProgress() {
    if (window.__broqCpsInit) return;
    var host = document.querySelector('[data-broq-cp-sticky]');
    if (!host) return; // no está en páginas que no sean colección
    window.__broqCpsInit = true;

    var UMBRAL_ENVIO = 1499;
    var UMBRAL_EXTRA = 4999;
    var PCT_EXTRA = 10;

    function render(cart) {
      host = document.querySelector('[data-broq-cp-sticky]');
      if (!host) return;
      var text = host.querySelector('[data-broq-cps-text]');
      var totalPesos = BroqCart.baseExtra(cart);
      if (cart.item_count === 0) {
        host.hidden = true;
        return;
      }
      host.hidden = false;
      var baseEnvio = (cart.total_price != null ? cart.total_price : cart.items_subtotal_price) / 100;
      var totalLabel = '$' + baseEnvio.toLocaleString('es-MX', { minimumFractionDigits: Number.isInteger(baseEnvio) ? 0 : 2, maximumFractionDigits: 2 });
      var msg = broqCartUnits(cart) + ' · ' + totalLabel;
      var bv = BroqCart.bienvenida(cart);
      if (baseEnvio < UMBRAL_ENVIO) {
        msg += ' · te faltan ' + BroqCart.fmt(Math.ceil(UMBRAL_ENVIO - baseEnvio)) + ' para envío gratis';
      } else if (BroqCart.tieneExtra(cart)) {
        msg += ' · 10% extra aplicado ✓';
      } else if (totalPesos < UMBRAL_EXTRA) {
        msg += ' · te faltan ' + BroqCart.fmt(Math.ceil(UMBRAL_EXTRA - totalPesos)) + ' para tu 10% extra';
      }
      // CRO §2 / Copy A: sufijo del código del pop-up.
      if (bv) msg += ' · tu código ✓';
      if (text && text.textContent !== msg) text.textContent = msg;
    }

    document.addEventListener(BroqCart.CART_LINES_UPDATE, function (event) {
      if (event && event.promise && typeof event.promise.then === 'function') {
        event.promise.then(function () { BroqCart.getCart().then(render); }).catch(function () {});
      } else {
        BroqCart.getCart().then(render);
      }
    });
  }

  /**
   * T1.5 (cro/IMPLEMENTACION.md) — Kits prearmados. El <form> de
   * broq-kit.liquid ya funciona SIN JS (action="/cart/add", multi-line
   * vía items[N][id]/items[N][quantity] — POST normal, navega a /cart).
   * Con JS interceptamos el submit para hacer el mismo multi-add por
   * fetch (sin navegar), disparar el evento real de carrito (así el
   * bubble del header se actualiza igual que con quick-add) y abrir el
   * Cart Drawer para confirmar — a diferencia de broq-quickadd, aquí SÍ
   * tiene sentido mostrar el carrito: agregar un kit completo es una
   * decisión de compra más grande que un quick-add de una tarjeta.
   */
  // ── ARMADOR DE PEDIDO — /pages/calculadora ──────────────────────────
  // Brief: retention 15-ago-2026 · decisiones en theme-dev/PLAN-ARMADOR.md.
  //
  // broqCalcPedido es la CAPA DE CÁLCULO COMPARTIDA de la escalera: el
  // armador la consume hoy; el quiz v3 migrará aquí la próxima vez que se
  // le meta mano (hoy tiene la suya y está vivo — no se toca por elegancia).
  //
  // Reglas duras del brief, encodeadas:
  //  · reventa = anclas guardadas por variante; nunca se recalculan desde price.
  //  · el 10% se evalúa sobre el subtotal de mercancía (así funciona el
  //    descuento automático: items_subtotal_price)
  //  · el envío se evalúa sobre el subtotal YA DESCONTADO (orden real de
  //    Shopify — la lección del cupón BIENVENIDA10 del 15-ago)
  //  · el MSI se evalúa sobre lo que va a pagar (total final)
  //  · aquí NO se redondea nada: el redondeo es del formateador, al mostrar
  //  · POPUP-10: bienvenidaPct = % del código LB5-/LB10- que Shopify YA aplicó
  //    en el carrito (BroqCart.bienvenida, solo si tiene monto asignado). No se
  //    combina con el 5% automático: gana el mayor (como en el checkout) y el
  //    envío se mide sobre el total con ESE descuento. Sin código: igual que hoy.
  function broqCalcPedido(cfg, lineas, bienvenidaPct) {
    // OFERTA NUEVA (CRO §7): 10% extra sobre productos a precio publicado (umbral ANTES
    // del código) y después el código sobre lo que queda (secuencial, nunca "el mayor").
    var subtotal = 0, resale = 0, hasAnchors = true, anchorReached = false;
    for (var i = 0; i < lineas.length; i++) {
      subtotal += lineas[i].precio * lineas[i].qty;
      if (Number.isFinite(lineas[i].ancla) && lineas[i].ancla > 0) {
        resale += lineas[i].ancla * lineas[i].qty;
        if (lineas[i].precio >= lineas[i].ancla) anchorReached = true;
      }
      else hasAnchors = false;
    }
    var tieneDiez = subtotal >= cfg.umbralExtra;
    var tras10 = tieneDiez ? subtotal * (1 - cfg.pctExtra / 100) : subtotal;
    var bv = subtotal > 0 && bienvenidaPct > 0 ? bienvenidaPct : 0;
    // LB5 no se suma con el 10% extra (combinesWith todo false): Shopify deja el mejor.
    if (bv && bv !== 10 && tieneDiez) bv = 0;
    var base = tras10 * (1 - bv / 100);
    var descuento = subtotal - base;
    var tieneEnvio = subtotal > 0 && base >= cfg.umbralEnvio;
    var envio = subtotal > 0 && !tieneEnvio ? cfg.costoEnvio : 0;
    var paga = base + envio;
    var canCompare = hasAnchors && !anchorReached;
    return {
      subtotal: subtotal,
      descuento: descuento,
      descuentoExtra: subtotal - tras10,
      descuentoCodigo: tras10 - base,
      envio: envio,
      paga: paga,
      vende: canCompare ? resale : null,
      gana: canCompare ? resale - paga : null,
      hasAnchors: hasAnchors,
      canCompare: canCompare,
      reviewReason: !hasAnchors ? 'missing_variant_anchor' : anchorReached ? 'wholesale_at_or_above_anchor' : '',
      tieneEnvio: tieneEnvio,
      tieneMsi: subtotal > 0 && subtotal >= cfg.umbralMsi,   // Copy 18:40: se anuncia sobre productos ≥ $2,999
      tieneDiez: tieneDiez,
      bienvenida: bv
    };
  }

  function initArmador() {
    var root = document.querySelector('[data-broq-armador]');
    if (!root || root.__broqArmInit) return;
    root.__broqArmInit = true;

    // Umbrales vivos del JSON de broq-config (la sección lo renderiza).
    // El fallback existe solo para no romper la página si el JSON faltara;
    // sus números vienen de UNIT-ECONOMICS §LA ESCALERA COMERCIAL.
    var cfg = { umbralEnvio: 1499, umbralMsi: 2999, umbralMsiReal: 2699.1, costoEnvio: 179, umbralExtra: 4999, pctExtra: 10 };
    try {
      var cfgEl = document.querySelector('[data-broq-config]');
      if (cfgEl) {
        var parsed = JSON.parse(cfgEl.textContent);
        for (var k in cfg) { if (parsed[k] != null) cfg[k] = parsed[k]; }
      }
    } catch (e) { /* fallback documentado */ }

    var discountLabel = root.querySelector('[data-arm-desc-label]');
    if (discountLabel) discountLabel.textContent = cfg.pctExtra + '% extra en todo tu pedido';
    var armBienvenida = 0;
    var anchors = {};
    var anchorMap = root.querySelector('[data-broq-catalog-anchors]');
    if (anchorMap) { try { anchors = JSON.parse(anchorMap.textContent || '{}'); } catch (e) {} }

    var FAMILIAS = [
      { handle: 'solitarios-de-zirconia', titulo: 'Solitarios de zirconia' },
      { handle: 'biselados', titulo: 'Biselados' },
      { handle: 'dormilonas', titulo: 'Dormilonas' },
      { handle: 'hongos', titulo: 'Hongos' },
      { handle: 'perlas', titulo: 'Perlas' },
      { handle: 'figuras', titulo: 'Figuras' },
      { handle: 'animales', titulo: 'Animales' },
      { handle: 'arracadas-y-choleras', titulo: 'Arracadas y choleras' }
    ];

    var catalogo = {};      // variantId → {precio, producto, presentacion, imagen}
    var estado = {};        // variantId → qty
    // El catálogo descargable conserva el mismo ancla guardada que la PDP.

    var elLista = root.querySelector('[data-arm-familias]');
    var elBusca = root.querySelector('[data-arm-buscar]');
    var elForm = root.querySelector('[data-broq-kit-form]');

    // Con centavos (22-oct-2026): mismo formato que la PDP y el carrito.
    function pesos(n) { return broqMoney(n); }

    function normal(s) {
      return String(s).toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    }

    function nombreCorto(t) {
      return String(t).replace(/^Broquel\s+/i, '').replace(/\s+Oro 10K$/i, '');
    }

    // ── catálogo vivo ──
    function cargarFamilia(fam) {
      return fetch('/collections/' + fam.handle + '/products.json?limit=250')
        .then(function (r) { return r.ok ? r.json() : { products: [] }; })
        .then(function (d) { return { fam: fam, products: d.products || [] }; })
        .catch(function () { return { fam: fam, products: [] }; });
    }

    function filaVariante(v, p, present) {
      var precio = parseFloat(v.price);
      catalogo[v.id] = {
        precio: precio,
        ancla: Number(anchors[v.id]) > 0 ? Number(anchors[v.id]) / 100 : null,
        producto: nombreCorto(p.title),
        presentacion: present,
        imagen: p.images && p.images[0] ? p.images[0].src : ''
      };
      return '<label class="broq-arm__var">' +
        '<span class="broq-arm__var-nombre">' + present + ' · <b>' + pesos(precio) + '</b></span>' +
        '<input type="number" min="0" value="0" inputmode="numeric" class="broq-arm__qty" data-arm-qty="' + v.id + '" aria-label="Cantidad ' + present.toLowerCase() + ' de ' + nombreCorto(p.title).replace(/"/g, '') + '">' +
        '</label>';
    }

    function pintarCatalogo(cargas) {
      var html = '';
      cargas.forEach(function (c, ci) {
        if (!c.products.length) return;
        var filas = '';
        c.products.forEach(function (p) {
          var par = null, pieza = null;
          (p.variants || []).forEach(function (v) {
            if (/par/i.test(v.title)) par = v;
            else pieza = v;
          });
          var img = p.images && p.images[0] ? p.images[0].src : '';
          filas += '<div class="broq-arm__fila" data-arm-nombre="' + normal(p.title) + '">' +
            (img ? '<img class="broq-arm__foto" src="' + img + (img.indexOf('?') > -1 ? '&' : '?') + 'width=96" width="48" height="48" loading="lazy" alt="">' : '<span class="broq-arm__foto"></span>') +
            '<div class="broq-arm__info"><p class="broq-arm__nombre">' + nombreCorto(p.title) + '</p><div class="broq-arm__vars">' +
            (par ? filaVariante(par, p, 'Par') : '') +
            (pieza ? filaVariante(pieza, p, 'Pieza') : '') +
            '</div></div></div>';
        });
        html += '<details class="broq-arm__familia"' + (ci === 0 ? ' open' : '') + '>' +
          '<summary>' + c.fam.titulo + ' <span class="broq-arm__cuenta">' + c.products.length + '</span></summary>' +
          '<div class="broq-arm__filas">' + filas + '</div></details>';
      });
      elLista.innerHTML = html || '<p class="broq-arm__error">No pudimos cargar el catálogo. Recarga la página o escríbenos.</p>';
    }

    // ── estado y pintura ──
    function lineas() {
      var out = [];
      for (var id in estado) {
        if (estado[id] > 0 && catalogo[id]) {
          out.push({ id: id, qty: estado[id], precio: catalogo[id].precio, ancla: catalogo[id].ancla, dato: catalogo[id] });
        }
      }
      return out;
    }

    function pintar() {
      var ls = lineas();
      var r = broqCalcPedido(cfg, ls, armBienvenida);
      if (ls.length && !r.canCompare) {
        var reviewKey = r.reviewReason + ':' + ls.map(function (line) { return line.id; }).join(',');
        if (root.__broqArmReferenceWarning !== reviewKey) {
          root.__broqArmReferenceWarning = reviewKey;
          console.warn('broq:psv:review-required', reviewKey);
          root.dispatchEvent(new CustomEvent('broq:psv:review-required', {
            bubbles: true, detail: { reason: r.reviewReason, variantIds: ls.map(function (line) { return line.id; }) }
          }));
        }
      } else { root.__broqArmReferenceWarning = ''; }
      var totalPares = 0;
      ls.forEach(function (l) { totalPares += l.qty; });

      var q = function (sel) { return root.querySelector(sel); };

      if (!ls.length) {
        q('[data-arm-ganancia]').textContent = '—';
        q('[data-arm-ganancia-pie]').textContent = 'Agrega modelos y aquí aparece tu ganancia estimada.';
      } else if (!r.hasAnchors) {
        q('[data-arm-ganancia]').textContent = '—';
        q('[data-arm-ganancia-pie]').textContent = 'Hay modelos sin precio sugerido de venta disponible.';
      } else if (!r.canCompare) {
        q('[data-arm-ganancia]').textContent = '—';
        q('[data-arm-ganancia-pie]').textContent = 'El precio sugerido de venta está en revisión.';
      } else if (r.gana <= 0) {
        q('[data-arm-ganancia]').textContent = 'Casi nada';
        q('[data-arm-ganancia-pie]').textContent = r.envio > 0
          ? 'Con un pedido tan chico, la guía de ' + pesos(r.envio) + ' se come lo que ganarías. Súbelo tantito y cambia mucho.'
          : 'El precio sugerido de venta está en revisión.';
      } else {
        q('[data-arm-ganancia]').textContent = pesos(r.gana);
        q('[data-arm-ganancia-pie]').textContent = 'Si vendes todo al precio que te sugerimos.';
      }

      q('[data-arm-pagas]').textContent = ls.length ? pesos(r.paga) : '$0';
      q('[data-arm-vendes]').textContent = ls.length && r.canCompare ? pesos(r.vende) : '—';
      q('[data-arm-conteo]').textContent = ls.length
        ? totalPares + (totalPares === 1 ? ' unidad · ' : ' unidades · ') + ls.length + (ls.length === 1 ? ' modelo' : ' modelos')
        : 'Nada todavía';

      q('[data-arm-d-merc]').textContent = pesos(r.subtotal);
      var dDesc = q('[data-arm-d-desc]');
      var conDesc = r.tieneDiez || r.bienvenida > 0;
      dDesc.textContent = conDesc ? '− ' + pesos(r.descuento) : 'No aplica';
      dDesc.className = conDesc ? 'broq-arm__verde' : '';
      if (discountLabel) discountLabel.textContent = r.bienvenida && r.tieneDiez ? '10% extra y tu código de primer pedido' : r.bienvenida ? 'Tu código de primer pedido' : cfg.pctExtra + '% extra en todo tu pedido';
      var dEnv = q('[data-arm-d-envio]');
      dEnv.textContent = !ls.length ? '—' : (r.tieneEnvio ? 'Gratis' : pesos(cfg.costoEnvio));
      dEnv.className = r.tieneEnvio && ls.length ? 'broq-arm__verde' : '';
      q('[data-arm-d-total]').textContent = pesos(r.paga);

      root.querySelector('[data-arm-p-envio]').classList.toggle('on', r.tieneEnvio);
      root.querySelector('[data-arm-p-msi]').classList.toggle('on', r.tieneMsi);
      root.querySelector('[data-arm-p-diez]').classList.toggle('on', r.tieneDiez);

      var emp = q('[data-arm-empujon]');
      var base = r.subtotal - r.descuento;
      if (!ls.length) {
        emp.hidden = true;
      } else if (!r.tieneEnvio) {
        emp.innerHTML = 'Te faltan <b>' + pesos(Math.ceil(cfg.umbralEnvio - base)) + '</b> para que el envío te salga gratis.';
        emp.hidden = false;
      } else if (!r.tieneMsi) {
        emp.innerHTML = 'Te faltan <b>' + pesos(Math.ceil(cfg.umbralMsi - r.subtotal)) + '</b> para poder pagarlo hasta en 3 meses sin intereses.';
        emp.hidden = false;
      } else if (!r.tieneDiez) {
        emp.innerHTML = 'Te faltan <b>' + pesos(Math.ceil(cfg.umbralExtra - r.subtotal)) + '</b> en productos para que te descontemos 10% extra.';
        emp.hidden = false;
      } else {
        emp.innerHTML = 'Ya traes <b>todo</b>: envío gratis, meses sin intereses y tu 10% extra.';
        emp.hidden = false;
      }

      // form del carrito: los hidden se sincronizan con el estado para que
      // initKitAdd (el camino probado de kits/orden-rápida) haga el multi-add
      var inputs = '';
      ls.forEach(function (l, i) {
        inputs += '<input type="hidden" name="items[' + i + '][id]" value="' + l.id + '">' +
          '<input type="hidden" name="items[' + i + '][quantity]" value="' + l.qty + '">';
      });
      elForm.querySelector('[data-arm-form-items]').innerHTML = inputs;
      var cta = document.querySelector('[data-broq-kit-add]');
      if (cta) cta.disabled = !ls.length;
      var desc = root.querySelector('[data-arm-descargas]');
      if (desc) desc.hidden = !ls.length;

      var mini = root.querySelector('[data-arm-mini]');
      if (mini) {
        mini.hidden = !ls.length;
        var miniB = mini.querySelector('b');
        if (miniB) {
          miniB.textContent = !ls.length || !r.canCompare ? '—' : (r.gana <= 0 ? 'casi nada' : pesos(r.gana));
        }
      }
    }

    // ── documentos ──
    // 🔑 Regla de oro (brief §6): el generador del CATÁLOGO recibe una
    // estructura SIN precios de mayoreo — se filtra aquí, no al pintar.
    function datosLista() {
      var ls = lineas(), canCompare = broqCalcPedido(cfg, ls, armBienvenida).canCompare;
      return ls.map(function (l) {
        return {
          modelo: l.dato.producto, presentacion: l.dato.presentacion, qty: l.qty,
          imagen: l.dato.imagen,
          pagasUnit: l.precio, pagasTotal: l.precio * l.qty,
          vende: canCompare ? l.ancla : null
        };
      });
    }
    function datosCatalogo() {
      var ls = lineas(), canCompare = broqCalcPedido(cfg, ls, armBienvenida).canCompare;
      return ls.map(function (l) {
        return {
          modelo: l.dato.producto, presentacion: l.dato.presentacion,
          imagen: l.dato.imagen,
          precioVenta: canCompare ? l.ancla : null
        };
      });
    }

    function fechaHoy() {
      return new Date().toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' });
    }

    function csv(filas, nombreArchivo) {
      var cuerpo = filas.map(function (f) {
        return f.map(function (c) { return '"' + String(c).replace(/"/g, '""') + '"'; }).join(',');
      }).join('\r\n');
      // BOM UTF-8: sin él, Excel en Windows destroza los acentos (Corazón →
      // CorazÃ³n). QA solo en Excel real — Numbers y Sheets no detectan el bug.
      var blob = new Blob(['﻿' + cuerpo], { type: 'text/csv;charset=utf-8' });
      var a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = nombreArchivo;
      document.body.appendChild(a);
      a.click();
      setTimeout(function () { URL.revokeObjectURL(a.href); a.remove(); }, 500);
    }

    function hoyArchivo() {
      var d = new Date();
      return d.getFullYear() + ('0' + (d.getMonth() + 1)).slice(-2) + ('0' + d.getDate()).slice(-2);
    }

    function descargaListaCsv() {
      var filas = [['Modelo', 'Presentación', 'Cantidad', 'Lo que pagas (c/u)', 'Lo que pagas (total)', 'Precio sugerido de venta (c/u)', 'Tu ganancia estimada (c/u)']];
      datosLista().forEach(function (d) {
        filas.push([d.modelo, d.presentacion, d.qty, Math.round(d.pagasUnit), Math.round(d.pagasTotal),
          d.vende == null ? 'No disponible' : Math.round(d.vende),
          d.vende == null ? 'No disponible' : Math.round(d.vende - d.pagasUnit)]);
      });
      filas.push([]);
      filas.push(['Precios sugeridos, no una promesa. Vigentes al ' + fechaHoy() + ' · broqueleria.com']);
      csv(filas, 'mi-lista-labroqueleria-' + hoyArchivo() + '.csv');
    }

    function descargaCatalogoCsv() {
      var nombre = (root.querySelector('[data-arm-nombre-input]').value || '').trim();
      var filas = [[nombre ? 'Catálogo de ' + nombre : 'Catálogo', '', '']];
      filas.push(['Modelo', 'Presentación', 'Precio']);
      datosCatalogo().forEach(function (d) {
        filas.push([d.modelo, d.presentacion, d.precioVenta == null ? 'No disponible' : '$' + d.precioVenta]);
      });
      filas.push([]);
      filas.push(['Precios vigentes al ' + fechaHoy()]);
      csv(filas, 'catalogo-' + hoyArchivo() + '.csv');
    }

    function imprimir(titulo, headHtml, filasHtml, pie) {
      var cont = document.getElementById('broq-arm-print');
      if (!cont) {
        cont = document.createElement('div');
        cont.id = 'broq-arm-print';
        document.body.appendChild(cont);
      }
      cont.innerHTML = '<h1>' + titulo + '</h1>' + headHtml + filasHtml +
        '<p class="broq-arm-print__pie">' + pie + '</p>';
      document.body.classList.add('broq-arm-imprimiendo');
      var fin = function () {
        document.body.classList.remove('broq-arm-imprimiendo');
        window.removeEventListener('afterprint', fin);
      };
      window.addEventListener('afterprint', fin);
      window.print();
    }

    function imprimeLista() {
      var filas = '<table><thead><tr><th></th><th>Modelo</th><th>Cant.</th><th>Pagas</th><th>Vendes en</th><th>Te queda</th></tr></thead><tbody>';
      datosLista().forEach(function (d) {
        filas += '<tr>' +
          '<td>' + (d.imagen ? '<img src="' + d.imagen + (d.imagen.indexOf('?') > -1 ? '&' : '?') + 'width=80" width="40" height="40">' : '') + '</td>' +
          '<td>' + d.modelo + ' · ' + d.presentacion + '</td><td>' + d.qty + '</td>' +
          '<td>' + pesos(d.pagasUnit) + '</td>' +
          '<td>' + (d.vende == null ? '—' : pesos(d.vende)) + '</td>' +
          '<td>' + (d.vende == null ? '—' : pesos(d.vende - d.pagasUnit)) + '</td></tr>';
      });
      filas += '</tbody></table>';
      imprimir('Mi lista de precios', '<p class="broq-arm-print__sub">Documento privado: trae lo que pagas. No lo compartas con tus clientas.</p>', filas,
        'Precios sugeridos, no una promesa. Vigentes al ' + fechaHoy() + ' · La Broquelería · broqueleria.com');
    }

    function imprimeCatalogo() {
      var nombre = (root.querySelector('[data-arm-nombre-input]').value || '').trim();
      var filas = '<table><thead><tr><th></th><th>Modelo</th><th>Precio</th></tr></thead><tbody>';
      datosCatalogo().forEach(function (d) {
        filas += '<tr>' +
          '<td>' + (d.imagen ? '<img src="' + d.imagen + (d.imagen.indexOf('?') > -1 ? '&' : '?') + 'width=80" width="40" height="40">' : '') + '</td>' +
          '<td>' + d.modelo + ' · ' + d.presentacion + '</td>' +
          '<td>' + (d.precioVenta == null ? '—' : '$' + d.precioVenta.toLocaleString('es-MX')) + '</td></tr>';
      });
      filas += '</tbody></table>';
      imprimir(nombre ? 'Catálogo de ' + nombre : 'Mi catálogo',
        '<p class="broq-arm-print__sub">Broqueles de oro 10k</p>', filas,
        'Precios vigentes al ' + fechaHoy());
    }

    // ── eventos ──
    root.addEventListener('input', function (e) {
      var qty = e.target.getAttribute && e.target.getAttribute('data-arm-qty');
      if (qty) {
        var n = parseInt(e.target.value, 10);
        estado[qty] = isNaN(n) || n < 0 ? 0 : n;
        pintar();
        return;
      }
      if (e.target === elBusca) {
        var t = normal(elBusca.value.trim());
        root.querySelectorAll('.broq-arm__fila').forEach(function (f) {
          f.hidden = t && f.getAttribute('data-arm-nombre').indexOf(t) === -1;
        });
        root.querySelectorAll('.broq-arm__familia').forEach(function (fam) {
          var visibles = fam.querySelectorAll('.broq-arm__fila:not([hidden])').length;
          fam.hidden = t && !visibles;
          if (t && visibles) fam.open = true;
        });
      }
    });

    root.addEventListener('click', function (e) {
      var chip = e.target.closest && e.target.closest('[data-arm-chip]');
      if (chip) {
        var ids = (chip.getAttribute('data-arm-chip') || '').split(',');
        ids.forEach(function (id) {
          id = id.trim();
          if (catalogo[id]) {
            estado[id] = Math.max(estado[id] || 0, 1);
            var input = root.querySelector('[data-arm-qty="' + id + '"]');
            if (input) input.value = estado[id];
          }
        });
        pintar();
        return;
      }
      var accion = e.target.closest && e.target.closest('[data-arm-descarga]');
      if (accion) {
        var tipo = accion.getAttribute('data-arm-descarga');
        if (tipo === 'lista-csv') descargaListaCsv();
        else if (tipo === 'lista-pdf') imprimeLista();
        else if (tipo === 'catalogo-csv') descargaCatalogoCsv();
        else if (tipo === 'catalogo-pdf') imprimeCatalogo();
      }
    });

    // POPUP-10: el armador no ve el carrito por sí solo; lee /cart.js y solo
    // usa un código LB5-/LB10- que Shopify ya aplicó. Si falla o no hay: 0.
    var armListo = false;
    function leerBienvenida() {
      return BroqCart.getCart().then(function (cart) {
        var b = BroqCart.bienvenida(cart);
        armBienvenida = b ? b.pct : 0;
        if (armListo) pintar();
      }).catch(function () {});
    }
    leerBienvenida();
    document.addEventListener(BroqCart.CART_LINES_UPDATE, function (event) {
      if (event && event.promise && typeof event.promise.then === 'function') {
        event.promise.then(leerBienvenida).catch(function () {});
      } else {
        leerBienvenida();
      }
    });

    // ── arranque ──
    Promise.all(FAMILIAS.map(cargarFamilia)).then(function (cargas) {
      pintarCatalogo(cargas);
      armListo = true;
      pintar();
    });
  }

  function initKitAdd() {
    if (window.__broqKitInit) return;
    window.__broqKitInit = true;

    document.addEventListener('submit', function (e) {
      var form = e.target.closest && e.target.closest('[data-broq-kit-form]');
      if (!form) return;
      e.preventDefault();
      // Los CTAs viven FUERA del <form> (via form="broq-kit-form" — hay
      // uno arriba y otro abajo desde el rediseño 30-jul); se buscan en
      // el documento y se deshabilitan/habilitan todos juntos.
      var btns = Array.prototype.slice.call(document.querySelectorAll('[data-broq-kit-add]'));
      if (!btns.length) btns = [form.querySelector('button[type="submit"]')].filter(Boolean);
      if (btns.some(function (b) { return b.disabled; })) return;
      var setDisabled = function (v) { btns.forEach(function (b) { b.disabled = v; }); };
      setDisabled(true);

      var items = [];
      var inputs = form.querySelectorAll('input[name^="items"][name$="[id]"]');
      inputs.forEach(function (input, idx) {
        var qtyInput = form.querySelector('input[name="items[' + idx + '][quantity]"]');
        var qty = qtyInput ? Number(qtyInput.value) : 1;
        // /orden-rapida (Fase 2) reusa este form con inputs de cantidad
        // editables — la mayoría de las celdas quedan en 0, no se agregan.
        if (qty > 0) items.push({ id: Number(input.value), quantity: qty });
      });
      if (!items.length) { setDisabled(false); return; }

      // MARTECH — `_lb_origen` (martech/implementacion/03-custom-conversions.md).
      // Los kits no son SKUs: sin esto, en la orden son idénticos a un carrito
      // armado a mano, y el Flow no puede restar el par 1125 de regalo (~$190)
      // que se agrega A MANO al empacar. Le enseñaríamos a Meta a perseguir kits
      // creyendo que dejan $190 más de lo que dejan.
      // Va por /cart/update.js y no dentro del add: /cart/add.js NO acepta
      // `attributes` (solo items/properties/sections — Shopify AJAX API).
      // Se escribe ANTES del add para que el atributo ya esté en el carrito
      // cuando aterricen las líneas; si falla, seguimos igual — el tracking
      // nunca bloquea una venta.
      var origen = form.getAttribute('data-broq-origen');
      var marcarOrigen = origen
        ? BroqCart.postJson('/cart/update.js', { attributes: { _lb_origen: origen } })
            .catch(function () {})
        : Promise.resolve();

      marcarOrigen
        .then(function () {
          return BroqCart.postJson('/cart/add.js', { items: items, sections: 'cart-drawer-section' });
        })
        .then(function (res) {
          setDisabled(false);
          if (!res.ok) { form.submit(); return; } // fallback: POST normal sin JS
          return BroqCart.getCart().then(function (cart) {
            BroqCart.dispatchCartEvent('add', 'kit', items.length, Promise.resolve(cart), 'broq-kit');
            // El trigger real es el <button on:click="#cart-drawer/toggle">
            // que envuelve <cart-icon> — el ícono en sí no tiene listener
            // propio (confirmado en vivo: cart-icon.click() no abría nada).
            var cartToggle = document.querySelector('[aria-controls="cart-drawer"]');
            if (cartToggle) cartToggle.click();
          });
        })
        .catch(function () {
          setDisabled(false);
          form.submit(); // fallback: si fetch falla por completo, POST normal
        });
    });
  }

  /**
   * Cintillos "Básico imprescindible" (negro, arriba-der) y "Más vendido"
   * (dorado, abajo-izq) como OVERLAY del tema sobre cada slide de la galería.
   * Solo si la PDP marcó [data-broq-cintillos] (producto con tag catalogo-real).
   * Se preservan en todas las fotos porque se inyectan en cada .product-media-container.
   */
  /**
   * Autoplay de los videos de la galería del producto — SOLO DESKTOP.
   *
   * Horizon envuelve cada video en <deferred-media>: el <video> vive dentro de
   * un <template> y no existe en el DOM hasta que alguien pulsa el póster. Eso
   * es justo lo que protege la carga inicial, así que NO se toca: en vez de
   * montar los videos al cargar, se montan cuando entran en pantalla.
   *
   * Coste de red: cero hasta que el video es visible. El <video> ya viene con
   * preload="metadata", muted y playsinline desde el tema, y al salir del
   * viewport se pausa para no gastar CPU ni datos de fondo.
   *
   * En móvil no se activa a propósito: son datos del usuario, y ahí el peso
   * del video sí se siente. Con prefers-reduced-motion tampoco.
   */
  function initVideoAutoplay(root) {
    if (!('IntersectionObserver' in window)) return;
    if (!window.matchMedia('(min-width: 750px)').matches) return;
    if (prefersReducedMotion()) return;

    var scope = root && root.querySelectorAll ? root : document;
    // OJO: se observa el CONTENEDOR, no el <deferred-media>. El deferred-media
    // mide 0 px de alto mientras su contenido sigue dentro del <template>, así
    // que un IntersectionObserver sobre él no dispara nunca.
    var medios = scope.querySelectorAll('.product-media');
    if (!medios.length) return;

    function activar(cont) {
      var dm = cont.querySelector('deferred-media');
      if (!dm) return;
      if (!dm.querySelector('video') && typeof dm.loadContent === 'function') {
        try { dm.loadContent(); } catch (e) { return; }
      }
      window.requestAnimationFrame(function () {
        var v = dm.querySelector('video');
        if (!v) return;
        v.muted = true; // sin esto el navegador bloquea el autoplay
        v.loop = true;
        v.setAttribute('playsinline', '');
        var pr = v.play();
        if (pr && typeof pr.catch === 'function') pr.catch(function () {});
      });
    }

    function visible(el) {
      var r = el.getBoundingClientRect();
      return r.height > 40 && r.top < window.innerHeight * 0.9 && r.bottom > 0;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            activar(entry.target);
          } else {
            var v = entry.target.querySelector('video');
            if (v && !v.paused) v.pause(); // fuera de pantalla no gasta red ni CPU
          }
        });
      },
      { threshold: 0.01 }
    );

    var conVideo = 0;
    var listos = 0;
    medios.forEach(function (cont) {
      if (!cont.querySelector('deferred-media')) return;
      conVideo++;
      if (visible(cont)) listos++;
      if (cont.__broqVideoObs) {
        // Ya observado, pero el IO no vuelve a disparar si el contenedor pasó
        // de 0 px a su tamaño real sin que hubiera scroll: se comprueba a mano.
        if (visible(cont) && !cont.querySelector('video')) activar(cont);
        return;
      }
      cont.__broqVideoObs = true;
      io.observe(cont);
      if (visible(cont)) activar(cont);
    });

    // La galería tarda en dimensionarse: hasta que no lo hace, TODOS los
    // contenedores miden 0 px y no hay nada que activar. En vez de adivinar el
    // retardo, se reintenta en intervalos cortos y se para en cuanto alguno
    // tiene tamaño real (o a los ~6 s, para no dejar un bucle vivo).
    if (conVideo && !listos) {
      window.__broqVideoTries = (window.__broqVideoTries || 0) + 1;
      if (window.__broqVideoTries < 15) {
        setTimeout(function () { initVideoAutoplay(root); }, 400);
      }
    }
  }

  /* ------------------------------------------------------------------ */
  /* Despacho: el corte manda sobre CUÁNDO SALE y CUÁNDO LLEGA           */
  /* ------------------------------------------------------------------ */
  // Sergio, 13-ago-2026: "El corte debe de aplicar a cuándo sale el pedido y
  // cuándo se entrega también." Antes la fecha de entrega contaba siempre
  // desde HOY, así que después del corte prometía un día menos del real: a
  // las 4pm del jueves seguía diciendo que salía ese jueves.
  //
  // Calendario (fuente única: snippets/broq-envio.liquid, leído del DOM):
  //   lunes a viernes → corte 15:00 · sábado → corte 13:00 · domingo → no sale
  //
  // Estas tres funciones las comparten el timer y la fecha de entrega a
  // propósito: si cada una tuviera su criterio, volverían a contradecirse.
  function broqCorteConfig() {
    var el = document.querySelector('[data-broq-corte]');
    if (!el) return null;
    var semana = parseInt(el.getAttribute('data-broq-corte'), 10);
    if (isNaN(semana)) return null;
    var sabado = parseInt(el.getAttribute('data-broq-corte-sabado'), 10);
    if (isNaN(sabado)) sabado = semana;
    return { semana: semana, sabado: sabado, domingo: el.getAttribute('data-broq-corte-domingo') === 'true' };
  }

  // Hora de corte del día d, o null si ese día no se despacha.
  function broqHoraCorte(cfg, d) {
    var w = d.getDay();
    if (w === 0) return cfg.domingo ? cfg.semana : null;
    if (w === 6) return cfg.sabado;
    return cfg.semana;
  }

  // El día en que sale el pedido si se compra en este momento.
  function broqDiaDespacho(cfg, ahora) {
    var h = broqHoraCorte(cfg, ahora);
    if (h !== null) {
      var corte = new Date(ahora);
      corte.setHours(h, 0, 0, 0);
      if (ahora < corte) return new Date(ahora);
    }
    var d = new Date(ahora);
    d.setHours(0, 0, 0, 0);
    do { d.setDate(d.getDate() + 1); } while (broqHoraCorte(cfg, d) === null);
    return d;
  }

  // n días hábiles DESPUÉS de start. La paquetería no entrega sábado ni
  // domingo, y eso es independiente de que nosotros sí despachemos el sábado.
  function broqDiasHabiles(start, n) {
    var x = new Date(start);
    var a = 0;
    while (a < n) {
      x.setDate(x.getDate() + 1);
      var w = x.getDay();
      if (w !== 0 && w !== 6) a++;
    }
    return x;
  }

  function broqPintarEta() {
    var eta = document.querySelector('[data-broq-eta]');
    if (!eta) return;
    var cfg = broqCorteConfig();
    // Sin config (otra plantilla, o el bloque no se renderizó) mantenemos el
    // comportamiento viejo: contar desde hoy. Nunca dejar la línea vacía.
    var base = cfg ? broqDiaDespacho(cfg, new Date()) : new Date();
    try {
      var d1 = broqDiasHabiles(base, 2);
      var d2 = broqDiasHabiles(base, 4);
      var txt;
      if (eta.hasAttribute('data-broq-eta-corto')) {
        // Formato corto de la urgencia en una línea (rediseño PDP 19-oct-2026):
        // "llega 21 a 23 oct"; si cruza de mes, "llega 30 oct a 3 nov".
        txt = 'llega ' + broqFechaCorta(d1, d1.getMonth() !== d2.getMonth()) + ' a ' + broqFechaCorta(d2, true);
      } else {
        var o = { day: 'numeric', month: 'long' };
        txt = 'Llega a tu domicilio entre el ' +
          d1.toLocaleDateString('es-MX', o) + ' y el ' +
          d2.toLocaleDateString('es-MX', o) + '.';
      }
      if (eta.textContent !== txt) eta.textContent = txt;
    } catch (e) {}
  }

  // "21 oct" / "21" (sin mes). Mes abreviado en es-MX sin el punto que
  // algunos motores le ponen ("oct." → "oct").
  function broqFechaCorta(d, conMes) {
    var dia = String(d.getDate());
    if (!conMes) return dia;
    var mes;
    try { mes = d.toLocaleDateString('es-MX', { month: 'short' }).replace('.', ''); }
    catch (e) { mes = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][d.getMonth()]; }
    return dia + ' ' + mes;
  }

  /* ------------------------------------------------------------------ */
  /* Carrusel de beneficios del banner de colección (solo móvil)         */
  /* ------------------------------------------------------------------ */
  // Sergio, 14-ago-2026: "¿podemos agregar unas flechas? Para que el usuario
  // pueda adelantar o cambiar el beneficio, y que dure dos segundos."
  //
  // El CSS ya rota solo con una animación de keyframes. Este JS la APAGA y
  // toma el control para poder mover con las flechas. La animación se queda
  // como respaldo a propósito: si este archivo no carga, los beneficios
  // siguen rotando (sin flechas, pero rotando) en vez de quedarse en uno.
  //
  // Solo actúa en móvil: en escritorio los tres se ven a la vez y mover el
  // transform rompería la rejilla.
  var ofertaCarruselController = null;

  function initOfertaCarrusel() {
    var car = document.querySelector('[data-broq-oferta-carrusel]');
    var track = car && car.querySelector('.broq-oferta__escalera');
    if (ofertaCarruselController) {
      if (ofertaCarruselController.car === car && ofertaCarruselController.track === track && car.isConnected) {
        ofertaCarruselController.sync();
        return;
      }
      ofertaCarruselController.destroy();
    }
    if (!car || !track) return;

    var TOTAL = 3; // el 4º peldaño es el clon que usa la animación de CSS
    var ESPERA = 4000; // ~4s por beneficio (Sergio, 21-ago: 2s "pasa bastante rápido"). El respaldo CSS (16s/4 tramos) va al mismo ritmo.
    var TRAS_TOCAR = 7000; // si la clienta usa las flechas, respiramos antes de reanudar
    var mq = window.matchMedia('(max-width: 899px)');
    var i = 0;
    var timer = null;
    var reanudar = null;
    var activo = false;
    var visible = false;
    var manualHasta = 0;
    var destruido = false;
    var visibilityObserver = null;
    var connectionObserver = null;

    function pintar() {
      track.style.transform = 'translateX(' + -100 * i + '%)';
    }

    function ir(n) {
      i = (n % TOTAL + TOTAL) % TOTAL;
      pintar();
    }

    function puedeRotar() {
      return !destruido && car.isConnected && activo && visible && !document.hidden && !prefersReducedMotion();
    }

    function parar() {
      if (timer) { clearInterval(timer); timer = null; }
      if (reanudar) { clearTimeout(reanudar); reanudar = null; }
    }

    function auto() {
      if (!puedeRotar()) { parar(); return; }
      var restante = manualHasta - Date.now();
      if (restante > 0) {
        if (!reanudar) {
          reanudar = setTimeout(function () { reanudar = null; auto(); }, restante);
        }
        return;
      }
      if (timer) return;
      timer = setInterval(function () {
        if (!puedeRotar()) { parar(); return; }
        ir(i + 1);
      }, ESPERA);
    }

    function manual(paso) {
      if (!activo || destruido || !car.isConnected) return;
      parar();
      ir(i + paso);
      manualHasta = Date.now() + TRAS_TOCAR;
      auto();
    }

    function onClick(event) {
      var btn = event.target.closest && event.target.closest('[data-broq-oferta-ir]');
      if (!btn || !car.contains(btn)) return;
      manual(parseInt(btn.getAttribute('data-broq-oferta-ir'), 10) || 1);
    }

    function sync() {
      if (destruido) return;
      if (!car.isConnected) { destroy(); return; }
      activo = mq.matches && !prefersReducedMotion();
      // Fuera de vista conservamos is-js: quitarla reactivaría el autoplay
      // de respaldo CSS. Escritorio y movimiento reducido recuperan su layout.
      car.classList.toggle('is-js', activo);
      if (activo) pintar();
      else track.style.transform = '';
      auto();
    }

    function destroy() {
      if (destruido) return;
      destruido = true;
      parar();
      if (visibilityObserver) visibilityObserver.disconnect();
      if (connectionObserver) connectionObserver.disconnect();
      car.removeEventListener('click', onClick);
      document.removeEventListener('visibilitychange', sync);
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else if (mq.removeListener) mq.removeListener(sync);
      if (reducedMotionQuery.removeEventListener) reducedMotionQuery.removeEventListener('change', sync);
      else if (reducedMotionQuery.removeListener) reducedMotionQuery.removeListener(sync);
      car.classList.remove('is-js');
      track.style.transform = '';
      if (ofertaCarruselController && ofertaCarruselController.car === car) ofertaCarruselController = null;
    }

    ofertaCarruselController = { car: car, track: track, sync: sync, destroy: destroy };
    car.addEventListener('click', onClick);
    document.addEventListener('visibilitychange', sync);
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else if (mq.addListener) mq.addListener(sync);
    if (reducedMotionQuery.addEventListener) reducedMotionQuery.addEventListener('change', sync);
    else if (reducedMotionQuery.addListener) reducedMotionQuery.addListener(sync);

    if ('IntersectionObserver' in window) {
      visibilityObserver = new IntersectionObserver(function (entries) {
        visible = entries.some(function (entry) { return entry.target === car && entry.isIntersecting; });
        sync();
      });
      visibilityObserver.observe(car);
    }
    // Sin IntersectionObserver las flechas siguen funcionando, sin autoplay.
    if ('MutationObserver' in window) {
      connectionObserver = new MutationObserver(function () {
        if (!car.isConnected || !track.isConnected || !car.contains(track)) initOfertaCarrusel();
      });
      connectionObserver.observe(document.body, { childList: true, subtree: true });
    }
    sync();
  }

  // Las estrellas del banner y de la tarjeta bajan a las reseñas de ESTA
  // página en vez de mandar a /pages/resenas (Sergio, 14-ago-2026: "es un
  // punto de fuga muy fuerte"). Va con scroll suave; el salto seco desde
  // arriba de una colección se siente como si te hubieran echado de la página.
  function initAnclaResenas() {
    if (window.__broqReviewAnchorInit) return;
    window.__broqReviewAnchorInit = true;
    document.addEventListener('click', function (ev) {
      var a = ev.target.closest ? ev.target.closest('a[href="#broq-resenas"]') : null;
      if (!a) return;
      var destino = document.getElementById('broq-resenas');
      if (!destino) return; // sin bloque en esta plantilla, que siga el enlace
      ev.preventDefault();
      destino.scrollIntoView({
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
        block: 'start'
      });
      // PDP (19-oct-2026): el "4.7" baja a las reseñas CON resaltado, no popup.
      // El ancla es un div vacío dentro de la sección de Klaviyo; se resalta
      // la sección que lo contiene (clase que el CSS §23 apaga sola).
      var sec = destino.closest('.shopify-section') || destino.parentElement;
      if (sec) {
        sec.classList.remove('broq-resenas--resaltado');
        void sec.offsetWidth; // reinicia la animación si se vuelve a tocar
        sec.classList.add('broq-resenas--resaltado');
        setTimeout(function () { sec.classList.remove('broq-resenas--resaltado'); }, 1800);
      }
    });
  }

  /* ------------------------------------------------------------ */
  /* Popups informativos de la PDP (cashback y garantía), 19-oct-2026 */
  /* snippets/broq-popup-info.liquid. Un solo componente: overlay,   */
  /* hoja desde abajo, X, "Entendido", Esc, clic fuera, foco atrapado */
  /* y sin scroll del fondo. Funcional: corre siempre (incluso PRM).  */
  /* ------------------------------------------------------------ */
  function initPopupInfo() {
    if (window.__broqPopupInfoInit) return;
    if (!document.querySelector('[data-broq-popup]')) return;
    window.__broqPopupInfoInit = true;

    var abierto = null;
    var opener = null;
    var FOCUSABLES = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

    function cerrar() {
      if (!abierto) return;
      abierto.hidden = true;
      abierto.classList.remove('is-open');
      document.documentElement.classList.remove('broq-popup-abierto');
      var volver = opener;
      abierto = null;
      opener = null;
      if (volver && typeof volver.focus === 'function') { try { volver.focus(); } catch (e) {} }
    }

    function abrir(id, quien) {
      var el = document.getElementById(id);
      if (!el || !el.hasAttribute('data-broq-popup')) return;
      if (abierto && abierto !== el) cerrar();
      abierto = el;
      opener = quien || null;
      el.hidden = false;
      document.documentElement.classList.add('broq-popup-abierto');
      // Dos frames: primero existe, luego anima (sin esto la transición no corre).
      requestAnimationFrame(function () { el.classList.add('is-open'); });
      var hoja = el.querySelector('[role="dialog"]');
      var primero = hoja ? hoja.querySelector(FOCUSABLES) : null;
      try { (primero || hoja || el).focus({ preventScroll: true }); } catch (e) {}
    }

    document.addEventListener('click', function (ev) {
      var t = ev.target;
      if (!t || !t.closest) return;
      var btnAbrir = t.closest('[data-broq-popup-open]');
      if (btnAbrir) {
        ev.preventDefault();
        abrir(btnAbrir.getAttribute('data-broq-popup-open'), btnAbrir);
        return;
      }
      if (abierto && t.closest('[data-broq-popup-close]') && abierto.contains(t)) {
        ev.preventDefault();
        cerrar();
      }
    });

    document.addEventListener('keydown', function (ev) {
      if (!abierto) return;
      if (ev.key === 'Escape') { ev.preventDefault(); cerrar(); return; }
      if (ev.key !== 'Tab') return;
      // Foco atrapado dentro de la hoja mientras el popup está abierto.
      var hoja = abierto.querySelector('[role="dialog"]') || abierto;
      var focs = hoja.querySelectorAll(FOCUSABLES);
      if (!focs.length) { ev.preventDefault(); return; }
      var primero = focs[0];
      var ultimo = focs[focs.length - 1];
      if (ev.shiftKey && (document.activeElement === primero || !hoja.contains(document.activeElement))) {
        ev.preventDefault(); ultimo.focus();
      } else if (!ev.shiftKey && (document.activeElement === ultimo || !hoja.contains(document.activeElement))) {
        ev.preventDefault(); primero.focus();
      }
    });
  }

  // Native product-form emits before POST and resolves after its cart GET.
  // Open only for a confirmed main-PDP add, once the native morph is ready.
  function initPdpCartAutoOpen() {
    if (window.__broqPdpCartOpenInit) return;
    window.__broqPdpCartOpenInit = true;
    var sequence = 0;
    var cancelPending = null;

    function cancel() {
      sequence++;
      if (cancelPending) cancelPending();
    }

    document.addEventListener('shopify:cart:lines-update', function (event) {
      cancel();
      var operation = sequence;
      var source = event.target;
      if (event.action !== 'add' || event.context !== 'product') return;
      if (!(source instanceof Element) || !source.matches('product-form-component')) return;
      if (!/\/products\/[^/]+\/?$/.test(window.location.pathname)) return;
      var productInfo = source.closest('.product-information');
      if (!productInfo || productInfo !== document.querySelector('main .product-information')) return;
      if (source.closest('dialog, featured-product-information')) return;
      if (!event.promise || typeof event.promise.then !== 'function') return;
      var trigger = document.activeElement;
      var sticky = trigger instanceof Element && trigger.closest('sticky-add-to-cart');
      if (!source.contains(trigger) && (!sticky || sticky.dataset.productId !== source.dataset.productId)) {
        trigger = source.querySelector('[ref="addToCartButton"]');
      }

      event.promise.then(function (result) {
        if (operation !== sequence || !source.isConnected) return;
        var detail = result && result.detail;
        if (!detail || detail.didError !== false || detail.source !== 'product-form-component') return;
        if (String(detail.sourceId) !== source.id || String(detail.productId) !== source.dataset.productId) return;
        if (!Array.isArray(detail.items) || !detail.items.length) return;
        var drawer = document.getElementById('cart-drawer');
        if (!drawer || drawer.hasAttribute('open') || !window.customElements || !window.MutationObserver) return;
        var expected = detail.items.map(function (item) { return { key: item.key, quantity: item.quantity }; });
        if (expected.some(function (item) { return !item.key || !(Number(item.quantity) > 0); })) return;
        var done = false;
        var observer;
        var timer;
        var frame;

        function cleanup() {
          done = true;
          if (observer) observer.disconnect();
          if (timer) clearTimeout(timer);
          if (frame) cancelAnimationFrame(frame);
          if (cancelPending === cleanup) cancelPending = null;
        }

        function visibleButton(button) {
          if (!(button instanceof HTMLElement) || !button.isConnected || button.disabled) return false;
          if (!button.getClientRects().length || getComputedStyle(button).visibility === 'hidden') return false;
          var rect = button.getBoundingClientRect();
          return rect.width > 0 && rect.height > 0 && rect.bottom > 0 && rect.right > 0 &&
            rect.top < window.innerHeight && rect.left < window.innerWidth;
        }

        function check() {
          if (done) return;
          if (operation !== sequence || !source.isConnected || !drawer.isConnected || drawer.hasAttribute('open')) {
            cleanup(); return;
          }
          if (typeof drawer.open !== 'function') return;
          var rows = Array.prototype.slice.call(drawer.querySelectorAll('tr.cart-items__table-row[data-key]'));
          if (rows.length !== expected.length) return;
          var ready = expected.every(function (item) {
            var row = rows.find(function (candidate) { return candidate.getAttribute('data-key') === item.key; });
            var input = row && row.querySelector('input[name="updates[]"]');
            return input && Number(input.value) === Number(item.quantity);
          });
          if (!ready) return;
          cleanup();
          // ThemeDrawer captures this element and restores it on close.
          var returnFocus = visibleButton(trigger) ? trigger : source.querySelector('[ref="addToCartButton"]');
          if (!visibleButton(returnFocus)) returnFocus = document.querySelector('[aria-controls="cart-drawer"]');
          if (visibleButton(returnFocus)) returnFocus.focus({ preventScroll: true });
          drawer.open();
        }

        cancelPending = cleanup;
        observer = new MutationObserver(check);
        observer.observe(drawer, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['value', 'open'] });
        timer = setTimeout(cleanup, 10000);
        customElements.whenDefined('theme-drawer').then(check);
        check();
        if (!done) frame = requestAnimationFrame(check);
      }).catch(function () { /* Native form owns the error; never repeat its POST. */ });
    });
    window.addEventListener('pagehide', cancel);
  }

  /* ------------------------------------------------------------ */
  /* Barra sticky de la PDP: cuando Horizon la muestra               */
  /* (data-stuck="true"), html.broq-sticky-on sube los flotantes     */
  /* (botón del cupón y burbuja de Gorgias) 64 px. Spec punto 21.    */
  /* ------------------------------------------------------------ */
  function initStickyWatch() {
    var bar = document.querySelector('.sticky-add-to-cart__bar');
    if (!bar) return;
    if (bar.__broqStickyInit) return;
    bar.__broqStickyInit = true;
    function sync() {
      var on = bar.getAttribute('data-stuck') === 'true';
      document.documentElement.classList.toggle('broq-sticky-on', on);
    }
    sync();
    if ('MutationObserver' in window) {
      new MutationObserver(sync).observe(bar, { attributes: true, attributeFilter: ['data-stuck'] });
    }
  }

  function initCorteEnvio() {
    // Timer real de corte de despacho (Sergio, 10-ago-2026). El HTML llega
    // oculto desde snippets/broq-envio.liquid porque el servidor no sabe la
    // hora real del visitante — todo el criterio de qué se muestra vive aquí.
    //
    // 🔴 13-ago-2026: pasado el corte este bloque NO se oculta, CAMBIA de
    // mensaje al siguiente día de despacho, y el timer se rearma solo pasada
    // la medianoche. El intento anterior de ocultarlo dejaba el guion de
    // relleno a la vista (`hidden` no puede contra el `display:flex` del CSS)
    // y, aun arreglando eso, un hueco también se lee como error.
    var el = document.querySelector('[data-broq-corte]');
    if (!el) return;
    if (el.__broqCorteInit) return;
    el.__broqCorteInit = true;
    var cfg = broqCorteConfig();
    if (!cfg) return;
    var msgHoy = el.querySelector('[data-broq-corte-hoy]');
    var msgManana = el.querySelector('[data-broq-corte-manana]');
    var texto = el.querySelector('[data-broq-corte-texto]');
    var dia = el.querySelector('[data-broq-corte-dia]');
    // Si falta cualquier pieza, no dejamos el bloque a medias: se queda oculto.
    if (!msgHoy || !msgManana || !texto || !dia) return;
    var iv = null;
    var to = null;
    var ultimoActivo = null;

    function etiquetaDia(ahora, d) {
      var manana = new Date(ahora);
      manana.setHours(0, 0, 0, 0);
      manana.setDate(manana.getDate() + 1);
      if (d.getTime() === manana.getTime()) return 'mañana';
      try { return 'el ' + d.toLocaleDateString('es-MX', { weekday: 'long' }); }
      catch (e) { return 'el siguiente día de envío'; }
    }

    function parar() {
      if (iv) { clearInterval(iv); iv = null; }
      if (to) { clearTimeout(to); to = null; }
    }

    function tick() {
      var ahora = new Date();
      var horaCorte = broqHoraCorte(cfg, ahora);
      var corte = null;
      if (horaCorte !== null) {
        corte = new Date(ahora);
        corte.setHours(horaCorte, 0, 0, 0);
      }
      var activo = corte !== null && ahora < corte;

      el.hidden = false;
      el.classList.toggle('broq-envio__corte--espera', !activo);
      msgHoy.hidden = !activo;
      msgManana.hidden = activo;

      // La fecha de entrega cuelga del mismo corte, así que se repinta al
      // cruzarlo — si no, una pestaña abierta desde antes se quedaría con la
      // fecha vieja mientras la línea de arriba ya dice "sale mañana".
      if (activo !== ultimoActivo) { ultimoActivo = activo; broqPintarEta(); }

      if (!activo) {
        dia.textContent = etiquetaDia(ahora, broqDiaDespacho(cfg, ahora));
        // Nada que contar hasta mañana: soltamos el intervalo de 1s y
        // reevaluamos justo pasada la medianoche. Así el timer "se reinicia"
        // solo, incluso si la pestaña lleva horas abierta.
        parar();
        var medianoche = new Date(ahora);
        medianoche.setHours(24, 0, 5, 0);
        to = setTimeout(function () { to = null; arrancar(); }, medianoche - ahora);
        return;
      }

      var msRestante = corte - ahora;
      var hh = Math.floor(msRestante / 3600000);
      var mm = Math.floor((msRestante % 3600000) / 60000);
      var ss = Math.floor((msRestante % 60000) / 1000);
      var partes = [];
      if (hh > 0) partes.push(hh + 'h');
      partes.push((mm < 10 ? '0' : '') + mm + 'm');
      // Los segundos solo se muestran dentro de la última hora — es donde
      // de verdad aporta sentido de urgencia; antes de eso solo parpadea sin decir nada útil.
      if (hh === 0) partes.push((ss < 10 ? '0' : '') + ss + 's');
      texto.textContent = partes.join(' ');
    }

    function arrancar() {
      parar();
      tick();
      // tick() deja `to` armado si estamos fuera de horario; en ese caso no
      // hace falta el intervalo de cada segundo.
      if (!iv && !to) iv = setInterval(tick, 1000);
    }

    arrancar();
  }

  /* ------------------------------------------------------------ */
  /* Popup "raspa y gana" — retention/BRIEF-POPUP-SCRATCH-DEV.md   */
  /* ------------------------------------------------------------ */
  function initPopupScratch() {
    var el = document.querySelector('[data-broq-scratch]');
    if (!el) return; // excluido por Liquid (checkout no renderiza el layout; /policies/* se gatea ahí)
    if (el.getAttribute('data-broq-scratch-init') === 'true') return;
    el.setAttribute('data-broq-scratch-init', 'true');

    // Viene de una campaña con su propio código: no le pises el descuento con el nuestro.
    if (/[?&]discount=/.test(window.location.search)) return;

    var SUPPRESS_KEY = 'broq_scratch_visto';
    var SUPPRESS_DIAS = 30;

    function leerCookie(nombre) {
      var m = document.cookie.match(new RegExp('(?:^|; )' + nombre + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    }
    function escribirCookie(nombre, valor, dias) {
      var exp = new Date();
      exp.setTime(exp.getTime() + dias * 24 * 60 * 60 * 1000);
      document.cookie = nombre + '=' + encodeURIComponent(valor) + '; expires=' + exp.toUTCString() + '; path=/; SameSite=Lax';
    }

    // "Visitante nuevo" se define por esta marca: si ninguno de los dos
    // almacenamientos responde, es imposible saber si ya lo vio — mejor
    // perder el lead que arriesgarnos a mostrarlo en cada carga (brief §2).
    var storageOk = false;
    var yaVisto = false;
    try {
      var vistoEn = Number(window.localStorage.getItem(SUPPRESS_KEY));
      yaVisto = vistoEn > 0 && Date.now() - vistoEn < SUPPRESS_DIAS * 86400000;
      if (vistoEn && !yaVisto) window.localStorage.removeItem(SUPPRESS_KEY);
      storageOk = true;
    } catch (e) { /* modo privado */ }
    if (!yaVisto) {
      try { yaVisto = !!leerCookie(SUPPRESS_KEY); storageOk = true; } catch (e) { /* bloqueado */ }
    }
    if (!storageOk || yaVisto) return;

    function marcarVisto() {
      try { window.localStorage.setItem(SUPPRESS_KEY, String(Date.now())); } catch (e) {}
      try { escribirCookie(SUPPRESS_KEY, '1', SUPPRESS_DIAS); } catch (e) {}
    }

    var codigo = el.getAttribute('data-codigo');
    var companyId = el.getAttribute('data-company-id');
    var listId = el.getAttribute('data-list-id');

    var canvas = el.querySelector('[data-broq-scratch-canvas]');
    var instruccion = el.querySelector('[data-broq-scratch-instruccion]');
    var premio = el.querySelector('[data-broq-scratch-premio]');
    var saltarBtn = el.querySelector('[data-broq-scratch-saltar]');
    var form = el.querySelector('[data-broq-scratch-form]');
    var errorEl = el.querySelector('[data-broq-scratch-error]');
    var copiarBtn = el.querySelector('[data-broq-scratch-copiar]');
    var ctx = canvas ? canvas.getContext('2d', { willReadFrequently: true }) : null;

    var disparado = false;
    var revelado = false;
    var raspando = false;
    var ultimoPunto = null;
    var muestreoPendiente = false;
    var focoAnterior = null;
    // Temporizador del hold de celebración. Se guarda para poder cancelarlo:
    // si el envío a Klaviyo contesta antes de que se cumplan los 2.6s, este
    // timeout pisaba la pantalla del código y regresaba al formulario.
    var timerForm = null;
    var aperturaPendiente = false;
    var observadorApertura = null;
    var frameReintento = null;
    var timerApertura = null;

    function pantallas() {
      return el.querySelectorAll('[data-broq-scratch-pantalla]');
    }
    function mostrarPantalla(nombre) {
      // Cualquier avance real cancela el hold pendiente: si ya se pasó de
      // pantalla, ese timeout solo puede hacer daño.
      if (timerForm && nombre !== 'form') { clearTimeout(timerForm); timerForm = null; }
      var lista = pantallas();
      for (var i = 0; i < lista.length; i++) {
        lista[i].hidden = lista[i].getAttribute('data-broq-scratch-pantalla') !== nombre;
      }
      // En móvil la foto se encoge SOLO en el formulario: es la pantalla
      // alta, y con la foto a 38vh el botón de enviar queda bajo el
      // pliegue. Las otras dos son cortas y se ven mejor con foto grande.
      el.classList.toggle('broq-scratch--compacto', nombre === 'form');
      var foco = el.querySelector('[data-broq-scratch-pantalla="' + nombre + '"] input, [data-broq-scratch-pantalla="' + nombre + '"] button');
      if (foco) foco.focus();
    }

    function elementosFocuseables() {
      var nodos = el.querySelectorAll('button:not([disabled]), [href], input:not([disabled]), select, [tabindex]:not([tabindex="-1"])');
      var visibles = [];
      for (var i = 0; i < nodos.length; i++) {
        if (nodos[i].offsetParent !== null) visibles.push(nodos[i]);
      }
      return visibles;
    }

    function syncScratchViewport() {
      if (el.hidden || !window.visualViewport) return;
      el.style.setProperty('--broq-scratch-viewport-height', window.visualViewport.height + 'px');
      el.style.setProperty('--broq-scratch-viewport-top', window.visualViewport.offsetTop + 'px');
    }

    function pantallaOcupada() {
      if (document.querySelector('dialog[open]')) return true;
      var activo = document.activeElement;
      if (!activo || el.contains(activo)) return false;
      if (activo.closest && activo.closest('dialog:not([open]), [hidden], [aria-hidden="true"]')) return false;
      if (activo.isContentEditable || (activo.closest && activo.closest('[contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"], [role="textbox"]'))) return true;
      if (activo.disabled || activo.readOnly) return false;
      if (activo.tagName === 'TEXTAREA' || activo.tagName === 'SELECT') return true;
      return activo.tagName === 'INPUT' && !/^(button|submit|reset|checkbox|radio|range|color|file|hidden)$/i.test(activo.type);
    }

    function limpiarEspera() {
      aperturaPendiente = false;
      document.removeEventListener('focusout', reintentarApertura, true);
      if (observadorApertura) { observadorApertura.disconnect(); observadorApertura = null; }
      if (frameReintento !== null) { cancelAnimationFrame(frameReintento); frameReintento = null; }
    }

    function reintentarApertura() {
      if (!aperturaPendiente || frameReintento !== null) return;
      // Deja terminar el foco/click y el showModal() nativo, que usa rAF.
      frameReintento = requestAnimationFrame(function () {
        frameReintento = requestAnimationFrame(function () {
          frameReintento = null;
          if (aperturaPendiente) abrir();
        });
      });
    }

    function esperarPantallaLibre() {
      if (aperturaPendiente) return;
      aperturaPendiente = true;
      document.addEventListener('focusout', reintentarApertura, true);
      observadorApertura = new MutationObserver(reintentarApertura);
      observadorApertura.observe(document.body, {
        childList: true, subtree: true, attributes: true, attributeFilter: ['open']
      });
    }

    function limpiarDisparadores() {
      limpiarEspera();
      if (timerApertura !== null) { clearTimeout(timerApertura); timerApertura = null; }
      document.removeEventListener('mouseout', intentoSalida);
    }

    function abrir() {
      if (disparado) return;
      if (pantallaOcupada()) { esperarPantallaLibre(); return; }
      limpiarDisparadores();
      disparado = true;
      focoAnterior = document.activeElement;
      el.hidden = false;
      document.documentElement.classList.add('broq-scratch-abierto');
      syncScratchViewport();
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', syncScratchViewport);
        window.visualViewport.addEventListener('scroll', syncScratchViewport);
      }
      marcarVisto();
      var enfocables = elementosFocuseables();
      if (enfocables[0]) enfocables[0].focus();
      // Doble rAF: al primer frame el panel ya existe pero a veces todavía
      // no terminó de layoutearse (visto en vivo con content-visibility del
      // tema, que a veces reporta 0 en el primer rect). iniciarRaspado()
      // además reintenta solo si el rect le sigue llegando en 0.
      requestAnimationFrame(function () { requestAnimationFrame(iniciarRaspado); });
    }

    function cerrar() {
      limpiarDisparadores();
      el.hidden = true;
      document.documentElement.classList.remove('broq-scratch-abierto');
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', syncScratchViewport);
        window.visualViewport.removeEventListener('scroll', syncScratchViewport);
      }
      el.style.removeProperty('--broq-scratch-viewport-height');
      el.style.removeProperty('--broq-scratch-viewport-top');
      if (focoAnterior && focoAnterior.focus) focoAnterior.focus();
    }

    /* ---- Canvas raspable ---- */
    function iniciarRaspado(intentos) {
      if (!canvas || !ctx) return;
      var rect = canvas.getBoundingClientRect();
      // El ancho/alto puede llegar en 0 si el panel aún no layoutea (ver
      // arriba). No fijar canvas.width/height con un rect en 0 — eso
      // dejaría el canvas trabado sin superficie para siempre. Reintenta
      // unos frames más antes de rendirse.
      if ((rect.width === 0 || rect.height === 0)) {
        intentos = (intentos || 0) + 1;
        if (intentos < 15) requestAnimationFrame(function () { iniciarRaspado(intentos); });
        return;
      }
      var dpr = window.devicePixelRatio || 1;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      // Sin canvas.style.width/height: el tamaño en pantalla ya lo define
      // el CSS (inset:0 + width/height:100%). Fijarlo aquí por inline
      // style pisaría esa regla para siempre si este rect llegara a leerse
      // mal — solo el buffer de dibujo (canvas.width/height) se toca.
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      pintarFoil(rect.width, rect.height);

      canvas.addEventListener('pointerdown', onPointerDown);
      canvas.addEventListener('pointermove', onPointerMove);
      canvas.addEventListener('pointerup', onPointerFin);
      canvas.addEventListener('pointercancel', onPointerFin);
      canvas.addEventListener('pointerleave', onPointerFin);
    }

    // Foil dorado: capas de degradado + destellos diagonales para que lea
    // como lámina metálica de raspar y no como un rectángulo café. Estos
    // tonos son SOLO de esta textura — el --broq-oro de marca se reserva
    // para acentos (DESIGN.md), no se usa como relleno de superficie.
    function pintarFoil(w, h) {
      ctx.globalCompositeOperation = 'source-over';

      var base = ctx.createLinearGradient(0, 0, w, h);
      base.addColorStop(0, '#A8823F');
      base.addColorStop(0.22, '#D8BE84');
      base.addColorStop(0.42, '#B08E4E');
      base.addColorStop(0.62, '#E0C994');
      base.addColorStop(1, '#9C7A3C');
      ctx.fillStyle = base;
      ctx.fillRect(0, 0, w, h);

      // Bandas de brillo en diagonal: el reflejo de una lámina real.
      ctx.save();
      ctx.globalAlpha = 0.16;
      ctx.strokeStyle = '#FFF6E2';
      for (var i = -h; i < w; i += 26) {
        ctx.lineWidth = (i % 52 === 0) ? 9 : 3;
        ctx.beginPath();
        ctx.moveTo(i, 0);
        ctx.lineTo(i + h, h);
        ctx.stroke();
      }
      ctx.restore();

      // Viñeta suave para que los bordes no compitan con el marco del panel.
      var vin = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.18, w / 2, h / 2, Math.max(w, h) * 0.72);
      vin.addColorStop(0, 'rgba(0,0,0,0)');
      vin.addColorStop(1, 'rgba(72,52,18,0.28)');
      ctx.fillStyle = vin;
      ctx.fillRect(0, 0, w, h);
    }

    function puntoRelativo(ev) {
      var rect = canvas.getBoundingClientRect();
      return { x: ev.clientX - rect.left, y: ev.clientY - rect.top };
    }
    function borrarEn(x, y) {
      ctx.globalCompositeOperation = 'destination-out';
      // Brocha ancha a propósito: con 22px raspar en desktop con el mouse
      // se sentía trabajoso (Sergio, 10-ago-2026).
      ctx.beginPath();
      ctx.arc(x, y, 32, 0, Math.PI * 2);
      ctx.fill();
    }
    function borrarLinea(p0, p1) {
      var dist = Math.hypot(p1.x - p0.x, p1.y - p0.y);
      var pasos = Math.max(1, Math.ceil(dist / 6));
      for (var i = 0; i <= pasos; i++) {
        var t = i / pasos;
        borrarEn(p0.x + (p1.x - p0.x) * t, p0.y + (p1.y - p0.y) * t);
      }
    }
    function onPointerDown(ev) {
      raspando = true;
      try { canvas.setPointerCapture(ev.pointerId); } catch (e) {}
      var p = puntoRelativo(ev);
      ultimoPunto = p;
      borrarEn(p.x, p.y);
      if (instruccion) instruccion.hidden = true;
      programarMuestreo();
    }
    function onPointerMove(ev) {
      if (!raspando) return;
      var p = puntoRelativo(ev);
      borrarLinea(ultimoPunto, p);
      ultimoPunto = p;
      programarMuestreo();
    }
    function onPointerFin() {
      raspando = false;
      ultimoPunto = null;
    }

    function programarMuestreo() {
      // Muestreo cada ~150ms mientras se raspa, no en cada evento de
      // movimiento (brief §5) — getImageData es caro, moverse no lo es.
      if (muestreoPendiente || revelado) return;
      muestreoPendiente = true;
      setTimeout(function () {
        muestreoPendiente = false;
        medirCobertura();
      }, 150);
    }
    function medirCobertura() {
      if (revelado) return;
      var datos;
      try { datos = ctx.getImageData(0, 0, canvas.width, canvas.height).data; } catch (e) { return; }
      var transparentes = 0, total = 0;
      for (var i = 3; i < datos.length; i += 16) { // canal alpha, 1 de cada 4px
        total++;
        if (datos[i] < 40) transparentes++;
      }
      // 38% y no 55%: raspar hasta la mitad se sentía trabajo, no premio
      // (Sergio, 10-ago-2026). El 10% ya se asoma desde la primera pasada
      // porque el premio vive debajo del canvas, así que al llegar aquí la
      // clienta ya sabe lo que ganó — esto solo limpia el resto.
      if (total > 0 && (transparentes / total) >= 0.38) revelarTodo();
    }
    function revelarTodo() {
      if (revelado) return;
      revelado = true;
      if (canvas) canvas.classList.add('broq-scratch__canvas--fade');
      if (instruccion) instruccion.hidden = true;
      if (premio) premio.classList.add('broq-scratch__premio--pop');
      lanzarConfeti();
      setTimeout(function () {
        if (canvas) canvas.hidden = true;
        if (premio) premio.classList.remove('broq-scratch__premio--pop');
      }, 320);
      // Se queda en el premio antes de pedir datos: si salta de inmediato,
      // el momento que justifica todo el raspado no existe (Sergio, 10-ago).
      timerForm = setTimeout(function () { timerForm = null; mostrarPantalla('form'); }, 2600);
    }

    // Confeti de foil: oro y marfil, nunca multicolor — el brief prohíbe la
    // estética de tómbola y eso sigue en pie; lo que se celebra es el premio,
    // no se convierte la marca en casino.
    function lanzarConfeti() {
      var lienzo = el.querySelector('[data-broq-scratch-confeti]');
      if (!lienzo || prefersReducedMotion()) return;
      var cctx = lienzo.getContext('2d');
      if (!cctx) return;

      var caja = el.getBoundingClientRect();
      var dpr = window.devicePixelRatio || 1;
      lienzo.width = caja.width * dpr;
      lienzo.height = caja.height * dpr;
      cctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      lienzo.classList.add('broq-scratch__confeti--activo');

      var origen = canvas ? canvas.getBoundingClientRect() : caja;
      var ox = origen.left - caja.left + origen.width / 2;
      var oy = origen.top - caja.top + origen.height / 2;
      var tonos = ['#9C7A3C', '#C9A661', '#E0C994', '#F7F3EC', '#0A0A0A'];
      var piezas = [];
      for (var i = 0; i < 70; i++) {
        var ang = (Math.PI * 2 * i) / 70 + Math.random() * 0.4;
        var vel = 4 + Math.random() * 7;
        piezas.push({
          x: ox, y: oy,
          vx: Math.cos(ang) * vel,
          vy: Math.sin(ang) * vel - 4,
          w: 4 + Math.random() * 5,
          h: 7 + Math.random() * 7,
          giro: Math.random() * Math.PI,
          vgiro: (Math.random() - 0.5) * 0.28,
          // La tinta es una pizca, no un color más: 1 de cada 5 lo más.
          color: tonos[Math.random() < 0.08 ? 4 : Math.floor(Math.random() * 4)]
        });
      }

      var cuadros = 0;
      function paso() {
        cuadros++;
        cctx.clearRect(0, 0, caja.width, caja.height);
        var vivos = 0;
        for (var j = 0; j < piezas.length; j++) {
          var p = piezas[j];
          p.vy += 0.22;      // gravedad
          p.vx *= 0.995;     // roce del aire
          p.x += p.vx;
          p.y += p.vy;
          p.giro += p.vgiro;
          if (p.y - p.h > caja.height) continue;
          vivos++;
          cctx.save();
          cctx.translate(p.x, p.y);
          cctx.rotate(p.giro);
          cctx.globalAlpha = Math.max(0, 1 - cuadros / 150);
          cctx.fillStyle = p.color;
          // Escalar en X simula el volteo de una hojuela de foil.
          cctx.fillRect(-p.w / 2, -p.h / 2, p.w * Math.abs(Math.cos(p.giro)), p.h);
          cctx.restore();
        }
        if (vivos > 0 && cuadros < 150) {
          requestAnimationFrame(paso);
        } else {
          cctx.clearRect(0, 0, caja.width, caja.height);
          lienzo.classList.remove('broq-scratch__confeti--activo');
        }
      }
      requestAnimationFrame(paso);
    }
    if (saltarBtn) saltarBtn.addEventListener('click', revelarTodo);

    /* ---- Cerrar / foco ---- */
    var cierres = el.querySelectorAll('[data-broq-scratch-close]');
    for (var c = 0; c < cierres.length; c++) cierres[c].addEventListener('click', cerrar);

    document.addEventListener('keydown', function (ev) {
      if (el.hidden) return;
      if (ev.key === 'Escape') { cerrar(); return; }
      if (ev.key !== 'Tab') return;
      var enfocables = elementosFocuseables();
      if (!enfocables.length) return;
      var primero = enfocables[0], ultimo = enfocables[enfocables.length - 1];
      if (ev.shiftKey && document.activeElement === primero) {
        ev.preventDefault(); ultimo.focus();
      } else if (!ev.shiftKey && document.activeElement === ultimo) {
        ev.preventDefault(); primero.focus();
      }
    });

    /* ---- Formulario -> Klaviyo ---- */
    if (form) {
      form.addEventListener('submit', function (ev) {
        ev.preventDefault();
        var nombre = (form.nombre.value || '').trim();
        var apellido = (form.apellido.value || '').trim();
        var email = (form.email.value || '').trim();
        var whatsapp = (form.whatsapp.value || '').trim();
        var perfilInput = form.querySelector('input[name="perfil"]:checked');
        if (!nombre || !apellido || !email || !perfilInput) return;
        // WhatsApp es obligatorio y con 10 dígitos (Sergio, 10-ago-2026).
        // Se valida aquí y no solo con `required` del HTML porque el form
        // lleva novalidate: sin esto un número a medias entraría a Klaviyo
        // como teléfono suscrito pero imposible de contactar.
        var whatsappDigitos = whatsapp.replace(/\D/g, '');
        if (whatsappDigitos.length !== 10) {
          if (errorEl) {
            errorEl.textContent = 'Escribe tu WhatsApp a 10 dígitos.';
            errorEl.hidden = false;
          }
          form.whatsapp.focus();
          return;
        }

        var submitBtn = form.querySelector('button[type="submit"]');
        if (submitBtn && submitBtn.disabled) return;
        var submitLabel = submitBtn ? submitBtn.textContent : '';
        if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Un momento…'; }
        if (errorEl) errorEl.hidden = true;

        broqSubscribeKlaviyo({
          companyId: companyId,
          listId: listId,
          email: email,
          phone: whatsappDigitos,
          firstName: nombre,
          lastName: apellido,
          // Suscrito a WhatsApp además de correo (Sergio, 10-ago-2026:
          // mandatorio y activado por default). Va de la mano con el texto
          // legal del snippet, que declara los DOS canales.
          optInWhatsapp: true,
          customSource: 'Popup raspa y gana',
          properties: (function () {
            var props = {
              origen: 'popup-scratch',
              perfil_negocio: perfilInput.value,
              consentimiento_marketing: true
            };
            // v1.5 del cashback (20-ago-2026): sin cupón no se estampa la
            // property — estampar BIENVENIDA10 en leads nuevos implicaría
            // una promesa que ya no hacemos y ensucia los segmentos de
            // retention. data-codigo llega vacío cuando el popup está en
            // modo cashback.
            if (codigo) props.cupon_bienvenida = codigo;
            return props;
          })()
        })
          .then(function (r) {
            if (!r.ok && r.status !== 202) throw new Error('klaviyo ' + r.status);
            // El consentimiento del form es obligatorio. El helper guarda
            // solo em/ph con los permisos publicitarios actuales de Shopify.
            broqSetUserData({
              email: email,
              phone: whatsappDigitos,
              firstName: nombre,
              lastName: apellido
            });
            // El único de los tres con perfil_negocio DECLARADO por la clienta.
            broqTrackLead('popup-scratch', { perfil_negocio: perfilInput.value });
            mostrarPantalla('codigo');
          })
          .catch(function () {
            // Restaura el mensaje genérico: la validación del WhatsApp de
            // arriba pudo haber dejado su propio texto en este mismo nodo.
            if (errorEl) {
              errorEl.textContent = 'Algo falló. Revisa tus datos e intenta de nuevo.';
              errorEl.hidden = false;
            }
          })
          .then(function () {
            if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitLabel; }
          });
      });
    }

    if (copiarBtn) {
      copiarBtn.addEventListener('click', function () {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(codigo).then(function () {
          var textoOriginal = copiarBtn.textContent;
          copiarBtn.textContent = 'Copiado';
          setTimeout(function () { copiarBtn.textContent = textoOriginal; }, 2000);
        }).catch(function () {});
      });
    }

    var irCatalogoBtn = el.querySelector('[data-broq-scratch-ir-catalogo]');
    if (irCatalogoBtn) irCatalogoBtn.addEventListener('click', cerrar);

    /* ---- Disparo: 5s o intento de salida, lo que ocurra primero ---- */
    function intentoSalida(ev) {
      if (!ev.relatedTarget && ev.clientY <= 0) abrir();
    }
    timerApertura = setTimeout(function () { timerApertura = null; abrir(); }, 5000);
    document.addEventListener('mouseout', intentoSalida);
  }

  function initCintillos(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var galleries = Array.from(scope.querySelectorAll('[data-broq-badge-gallery]'));
    if (scope.matches && scope.matches('[data-broq-badge-gallery]')) galleries.unshift(scope);
    galleries.forEach(function (gallery) {
      gallery.querySelectorAll('[data-broq-gallery-badges]').forEach(function (overlay) {
        var media = overlay.closest('.product-media-container');
        var img = media && media.querySelector('img');
        if (!media || !img) return;
        var box = media.getBoundingClientRect();
        if (!box.width || !box.height) return;
        var rect = img.getBoundingClientRect();
        var width = rect.width, height = rect.height;
        var fit = getComputedStyle(img).objectFit;
        if ((fit === 'contain' || fit === 'scale-down') && img.naturalWidth && img.naturalHeight) {
          var ratio = Math.min(rect.width / img.naturalWidth, rect.height / img.naturalHeight);
          width = img.naturalWidth * ratio;
          height = img.naturalHeight * ratio;
        }
        var left = rect.left + (rect.width - width) / 2;
        var top = rect.top + (rect.height - height) / 2;
        var category = overlay.querySelector('.broq-cintillo--basico');
        var discount = overlay.querySelector('[data-broq-discount-badge]');
        if (category) {
          category.style.top = Math.max(0, top - box.top) + 12 + 'px';
          category.style.right = Math.max(0, box.right - left - width) + 12 + 'px';
        }
        if (discount) {
          discount.style.bottom = Math.max(0, box.bottom - top - height) + 12 + 'px';
          discount.style.left = Math.max(0, left - box.left) + 12 + 'px';
          broqSyncDiscountBadge(discount);
        }
        if (!img.__broqCintilloLoad) {
          img.__broqCintilloLoad = true;
          img.addEventListener('load', function () { initCintillos(gallery); });
        }
      });
    });
    if (!window.__broqCintilloBound) {
      window.__broqCintilloBound = true;
      window.addEventListener('resize', function () { initCintillos(document); });
      window.addEventListener('load', function () { initCintillos(document); });
    }
  }

  // Refuerzo de 1 línea mostrado al ENTRAR al siguiente paso, citando la
  // respuesta anterior (técnica de "personalización barata" — mismo kit,
  // distinto narrativo — confirmada en research de quizzes DTC de alta
  // conversión: Splitbase / Digioh×Obvi / Octane AI, 2026-07-30).
  // Quiz v3 (2-ago-2026, QUIZ-V3-PROPUESTA.md): 4 preguntas que SÍ deciden,
  // presupuesto como techo duro, 6 kits, gate con email + guía PDF.
  // Refuerzo mostrado al ENTRAR al siguiente paso, citando la respuesta.
  var QUIZ_REFUERZO = {
    trayectoria: {
      nada: 'Todas las que hoy venden bien empezaron justo donde estás tú.',
      catalogo: 'Ya sabes vender — ahora con un producto que deja más.',
      oro: 'Perfecto — hablemos de proveedor a negocio.'
    },
    canal: {
      conmigo: 'Cero renta: WhatsApp y redes, pura ganancia.',
      local: 'El oro real sube el ticket de cualquier local.',
      joyeria: 'Profundidad de catálogo, entonces. Vamos.'
    },
    clientela: {
      mujeres: 'Anotado — mujeres, para ellas o para regalar.',
      ninas: 'Anotado — bautizo y primer arete.',
      todo: 'Anotado — surtido variado.'
    },
    presupuesto: {
      prueba: 'Empezar con poco es empezar bien.',
      b1: 'Con eso armas una base sólida.',
      b2: 'Tu pedido ya llega a meses sin intereses: hasta 3, con tarjetas participantes.',
      b3: 'Te toca el 10% extra en todo tu pedido en compras +$4,999. Se aplica solo.'
    }
  };

  var QUIZ_INTRO_TRAYECTORIA = {
    nada: 'Empezar de cero es más simple de lo que parece: el inventario correcto y tu círculo de confianza.',
    catalogo: 'Ya tienes clientela y músculo de venta — el salto a oro real es subir de margen, no empezar de cero.',
    oro: 'Ya vendes oro: pruébanos como proveedor — oro 10k macizo, fotos que puedes descargar y usar, y resurtido sin viajar.'
  };

  // ⚠️ Superficie con datos COPIADOS de los kits v2 (bundles): precio, pares
  // y ganancia se actualizan junto con los PRODUCTOS kit en cada reprecio
  // (checklist §9 de UNIT-ECONOMICS.md). VIVA en /pages/arma-tu-pedido
  // (page.quiz vía template suffix — NO hay /pages/quiz).
  //
  // La ganancia se carga desde el ancla guardada y la composición aprobada del kit.
  var QUIZ_KITS = {
    k5: { titulo: 'Kit 5 Pares', pares: 5, precio: '$1,786.79', regalo: false,
      texto: 'Los 5 pares que más se venden de toda la tienda: arrancas con lo ya comprobado, con envío gratis y cashback.',
      url: '/products/kit-5-pares', gan: null,
      img: 'https://cdn.shopify.com/s/files/1/0735/1120/6998/files/lb-1125-hero.png?width=600',
      pdf: '' },
    k7: { titulo: 'Kit 7 Pares', pares: 7, precio: '$2,484.79', regalo: true,
      texto: 'Los 5 más vendidos, más Cruz Lisa y Luna y Estrella: más variedad para ofrecer, con envío gratis y par de regalo.',
      url: '/products/kit-7-pares', gan: null,
      img: 'https://cdn.shopify.com/s/files/1/0735/1120/6998/files/lb-1431-hero.png?width=600',
      pdf: '' },
    k10: { titulo: 'Kit 10 Pares', pares: 10, precio: '$3,795.79', regalo: true,
      texto: 'Surtido completo en un solo pedido: 10 modelos distintos, a 3 meses sin intereses y con par de regalo.',
      url: '/products/kit-10-pares', gan: null,
      img: 'https://cdn.shopify.com/s/files/1/0735/1120/6998/files/lb-C110-hero.png?width=600',
      pdf: '' },
    joyeria: { titulo: 'Kit Joyería · 16 Pares', pares: 16, precio: '$6,086.79 → pagas $5,478.12', regalo: true,
      texto: '16 pares: el surtido más completo que armamos, para la que repone en serio.',
      url: '/products/kit-joyeria-16-pares', gan: null,
      img: 'https://cdn.shopify.com/s/files/1/0735/1120/6998/files/kitflat-kit-joyeria.png?width=600',
      pdf: '' }
  };

  var QUIZ_STEP_ORDER = ['trayectoria', 'canal', 'clientela', 'presupuesto', 'lead', 'resultado'];
  var QUIZ_PROGRESO = {
    trayectoria: { pct: 12, label: 'Pregunta 1 de 4' },
    canal: { pct: 32, label: 'Pregunta 2 de 4' },
    clientela: { pct: 52, label: 'Pregunta 3 de 4' },
    presupuesto: { pct: 72, label: 'Pregunta 4 de 4' },
    lead: { pct: 90, label: 'Último paso' },
    resultado: { pct: 100, label: '' }
  };

  // Matriz v3: el PRESUPUESTO es techo duro — jamás recomendar arriba de
  // la banda declarada (bug v2: bajo+variedad → kit de $5,384).
  function quizElegirKit(r) {
    // Kits v2 (24-ago): el techo se evalúa sobre el precio de LISTA del kit.
    if (r.presupuesto === 'prueba') return 'k5';
    if (r.presupuesto === 'b1') return 'k5';
    if (r.presupuesto === 'b2') return 'k7';
    return (r.trayectoria === 'oro' && r.canal === 'joyeria') ? 'joyeria' : 'k10';
  }

  // Etiquetas para el bloque "por qué este kit" del resultado.
  var QUIZ_LABEL = {
    trayectoria: { nada: 'estás empezando', catalogo: 'ya vendes por catálogo o redes', oro: 'ya vendes joyería de oro' },
    canal: { conmigo: 'venderás por WhatsApp y redes', local: 'venderás en tu local', joyeria: 'surtes una joyería' },
    clientela: { mujeres: 'tu clientela son mujeres', ninas: 'tu fuerte son bebés y niñas', todo: 'quieres surtido variado' },
    presupuesto: { prueba: 'quieres empezar con poco', b1: 'inviertes $2,000–3,000', b2: 'inviertes $3,000–5,000', b3: 'inviertes más de $5,000' }
  };

  function broqEsc(s) {
    return String(s || '').replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Instrumentación del funnel (v2 era caja negra). dataLayer siempre;
  // cuando martech monte GTM/pixel, estos eventos ya están listos.
  function broqQuizTrack(evento, props) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: evento }, props || {}));
    } catch (e) { /* nunca romper el quiz por tracking */ }
  }

  // CTA "Toma el quiz" del header: el item del menú (oculto por CSS en
  // desktop) se reubica como pill dentro de <header-actions>, junto a los
  // íconos — el DOM del header (overflow-list display:block) no permite
  // empujarlo a la derecha por CSS puro. En mobile vive en el drawer.
  function initHeaderQuizCta() {
    if (window.__broqQuizCtaInit) return;
    var actions = document.querySelector('header-actions');
    if (!actions) return;
    window.__broqQuizCtaInit = true;
    var cta = document.createElement('a');
    cta.href = '/pages/arma-tu-pedido';
    cta.className = 'broq-header-quiz-cta';
    cta.textContent = 'Toma el quiz';
    actions.insertBefore(cta, actions.firstChild);
  }

  function initQuiz() {
    var root = document.querySelector('[data-broq-quiz]');
    if (!root || window.__broqQuizInit) return;
    window.__broqQuizInit = true;

    var respuestas = {};
    var progresoFill = root.querySelector('[data-broq-quiz-progreso]');
    var progresoZona = root.querySelector('[data-broq-quiz-progreso-zona]');
    var pasoLabel = root.querySelector('[data-broq-quiz-paso]');
    var avanzando = false; // guarda contra doble click durante el flash de selección

    function showStep(name) {
      root.querySelectorAll('[data-broq-quiz-step]').forEach(function (step) {
        step.hidden = step.getAttribute('data-broq-quiz-step') !== name;
      });
      // reset de selección visual al entrar a un paso (para el flujo back→forward)
      root.querySelectorAll('.broq-quiz__opcion.is-selected').forEach(function (b) {
        b.classList.remove('is-selected');
      });
      var prog = QUIZ_PROGRESO[name];
      if (progresoFill && prog) progresoFill.style.width = prog.pct + '%';
      if (pasoLabel && prog) pasoLabel.textContent = prog.label;
      if (progresoZona) progresoZona.style.visibility = name === 'resultado' ? 'hidden' : '';
      avanzando = false;
      broqQuizTrack('quiz_paso', { paso: name });
      // En mobile la usuaria queda scrolleada abajo tras elegir la última
      // tarjeta — regresa al inicio del quiz en cada cambio de paso.
      try {
        root.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
      } catch (e) { /* ok */ }
    }

    function pintarRefuerzo(paso, texto) {
      var el = root.querySelector('[data-broq-quiz-refuerzo="' + paso + '"]');
      if (el) el.textContent = texto || '';
    }

    root.addEventListener('click', function (e) {
      var back = e.target.closest('[data-broq-quiz-back]');
      if (back) { showStep(back.getAttribute('data-goto')); return; }

      var choice = e.target.closest('[data-broq-quiz-choice]');
      if (!choice || avanzando) return;
      avanzando = true;
      var campo = choice.getAttribute('data-campo');
      var valor = choice.getAttribute('data-valor');
      respuestas[campo] = valor;

      var goto = choice.getAttribute('data-goto');
      var refMapa = QUIZ_REFUERZO[campo];
      if (refMapa && refMapa[valor]) pintarRefuerzo(goto, refMapa[valor]);
      broqQuizTrack('quiz_respuesta', { campo: campo, valor: valor });

      // Flash de selección (tarjeta se invierte) antes de avanzar — patrón
      // Obvi. Con prefers-reduced-motion se avanza de inmediato.
      choice.classList.add('is-selected');
      var delay = prefersReducedMotion() ? 0 : 280;
      setTimeout(function () { showStep(goto); }, delay);
    });

    var leadForm = root.querySelector('[data-broq-quiz-lead-form]');
    if (leadForm) {
      leadForm.addEventListener('submit', function (e) {
        e.preventDefault();
        respuestas.nombre = (leadForm.nombre.value || '').trim();
        respuestas.email = (leadForm.email.value || '').trim();
        respuestas.telefono = (leadForm.telefono.value || '').trim();
        var consiente = !!(leadForm.consiente_marketing && leadForm.consiente_marketing.checked);

        var kitKey = quizElegirKit(respuestas);
        var kit = QUIZ_KITS[kitKey];

        // Upsert directo del perfil (client/profiles): garantiza que el lead
        // y sus respuestas aterricen SIEMPRE (lección 30-jul: suscripción a
        // canal no habilitado se descarta en silencio). Email ahora es
        // obligatorio — el único canal conectado hoy. El consent de
        // marketing es OPCIONAL y va como evidencia en properties.
        fetch('https://a.klaviyo.com/client/profiles/?company_id=' + encodeURIComponent(root.getAttribute('data-company-id')), {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', 'revision': '2025-01-15' },
          body: JSON.stringify({
            data: {
              type: 'profile',
              attributes: {
                email: respuestas.email,
                phone_number: '+52' + respuestas.telefono.replace(/\D/g, ''),
                first_name: respuestas.nombre,
                properties: {
                  origen: 'quiz-v3',
                  marca: 'La Broqueleria',
                  quiz_trayectoria: respuestas.trayectoria,
                  quiz_canal: respuestas.canal,
                  quiz_clientela: respuestas.clientela,
                  quiz_presupuesto: respuestas.presupuesto,
                  quiz_kit_v3: kitKey,
                  quiz_experiencia: respuestas.trayectoria === 'oro' ? 'experta' : 'nueva',
                  guia_pdf: kit.pdf || '',
                  consentimiento_marketing: consiente,
                  consentimiento_fecha: new Date().toISOString()
                }
              }
            }
          })
        }).then(function (res) {
          broqQuizTrack(res && res.ok ? 'quiz_lead_ok' : 'quiz_lead_error', { kit: kitKey, status: res && res.status });
          if (res && res.ok) {
            // Enum real de `trayectoria` verificado en sections/broq-quiz.liquid:
            // solo existen 'nada' | 'catalogo' | 'oro'. 'oro' = ya vende joyería
            // de oro → ya-vende. 'catalogo' vende por catálogo o redes pero no
            // joyería, y 'nada' va empezando → los dos son por-empezar.
            // Esta casilla opcional también condiciona la captura; el helper
            // comprueba los permisos publicitarios de Shopify en todos los forms.
            if (consiente) {
              broqSetUserData({
                email: respuestas.email,
                phone: respuestas.telefono,
                firstName: respuestas.nombre
              });
            }
            // El quiz NUNCA produce 'personal': no tiene esa opción, así que
            // el 100% de sus leads entra a `LB – Lead MAYOREO`, que es lo
            // correcto — es un quiz de mayoreo.
            broqTrackLead('quiz', {
              perfil_negocio: respuestas.trayectoria === 'oro' ? 'ya-vende' : 'por-empezar',
              quiz_trayectoria: respuestas.trayectoria,
              quiz_kit: kitKey
            });
          }
        }).catch(function () {
          broqQuizTrack('quiz_lead_error', { kit: kitKey, status: 'network' });
        });

        // Suscripción a la lista SOLO si marcó el consent opcional (email
        // aterriza hoy; WhatsApp aplicará cuando el canal esté conectado).
        if (consiente) {
          broqSubscribeKlaviyo({
            companyId: root.getAttribute('data-company-id'),
            listId: root.getAttribute('data-list-id'),
            email: respuestas.email,
            phone: respuestas.telefono,
            customSource: 'Quiz v3 — arma tu pedido',
            properties: { origen: 'quiz-v3', marca: 'La Broqueleria', quiz_kit_v3: kitKey }
          }).catch(function () { /* el resultado se muestra igual */ });
        }

        // ---------- Resultado v3: el reveal paga el gate ----------
        var porque = [];
        ['trayectoria', 'canal', 'clientela', 'presupuesto'].forEach(function (campo) {
          var l = QUIZ_LABEL[campo] && QUIZ_LABEL[campo][respuestas[campo]];
          if (l) porque.push(l);
        });
        var notaExtra = '';
        var intro = QUIZ_INTRO_TRAYECTORIA[respuestas.trayectoria] || '';
        var saludo = respuestas.nombre ? broqEsc(respuestas.nombre) + ', tu' : 'Tu';
        var statsRegalo = kit.regalo ? ' · incluye <strong>1 par de regalo</strong>' : '';
        var target = root.querySelector('[data-broq-quiz-resultado]');
        target.innerHTML =
          '<div class="broq-quiz__resultado-card">' +
          '<span class="broq-quiz__resultado-sparkle" aria-hidden="true">✦</span>' +
          '<p class="broq-quiz__resultado-intro">' + saludo + ' kit ideal es:</p>' +
          '<h2 class="broq-quiz__resultado-titulo">' + kit.titulo + '</h2>' +
          '<img class="broq-quiz__resultado-img" src="' + kit.img + '" alt="' + kit.titulo + '" loading="lazy">' +
          '<p class="broq-quiz__resultado-stats"><strong>' + kit.pares + ' pares</strong> · ' + kit.precio + statsRegalo + '</p>' +
          '<p class="broq-quiz__resultado-texto">' + intro + ' ' + kit.texto + '</p>' +
          '<p class="broq-quiz__resultado-porque">Por qué este kit: ' + porque.join(' · ') + '.</p>' +
          '<p class="broq-quiz__resultado-gan" data-quiz-gain hidden></p>' +
          '<a class="broq-quiz__resultado-cta" href="' + kit.url + '">Ver ' + kit.titulo + ' →</a>' +
          (kit.pdf ? '<a class="broq-quiz__resultado-pdf" href="' + kit.pdf + '" target="_blank" rel="noopener">Descargar mi guía de reventa (PDF) ↓</a>' : '') +
          notaExtra +
          '<a class="broq-quiz__resultado-secundario" href="/collections/all">o explora todo el catálogo →</a>' +
          '</div>';
        var gainTarget = target.querySelector('[data-quiz-gain]');
        function loadFixedKitGain() {
          if (!window.BroqKitFigures || !gainTarget || !gainTarget.isConnected) return;
          var variantMap = {}, config = {};
          try {
            variantMap = JSON.parse(document.querySelector('[data-broq-quiz-variants]').textContent);
            config = JSON.parse(document.querySelector('[data-broq-config]').textContent);
          } catch (e) { return; }
          var handle = kit.url.split('/').pop(), variantId = variantMap[handle];
          if (!variantId) return;
          window.BroqKitFigures.load({
            id: 'gid://shopify/ProductVariant/' + variantId,
            country: root.getAttribute('data-kit-country'), currency: root.getAttribute('data-kit-currency'), cfg: config
          }).then(function (figures) {
            if (!gainTarget.isConnected || figures.gain === null) return;
            gainTarget.textContent = 'Ganancia estimada revendiendo al precio sugerido: ' + broqMoney(figures.gain / 100) + '.';
            gainTarget.hidden = false;
          }).catch(function () { /* sin referencia verificada, se omite la ganancia */ });
        }
        if (window.BroqKitFigures) loadFixedKitGain();
        else window.addEventListener('broq:kit-figures:ready', loadFixedKitGain, { once: true });
        broqQuizTrack('quiz_resultado', { kit: kitKey });
        showStep('resultado');
      });
    }
  }

  function initCollectionCierre() {
    var root = document.querySelector('[data-broq-cierre-coleccion]');
    if (!root || window.__broqCierreInit) return;
    window.__broqCierreInit = true;

    var form = root.querySelector('[data-broq-cierre-form]');
    var confirmacion = root.querySelector('[data-broq-cierre-confirmacion]');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (form.email.value || '').trim();
      var telefono = (form.telefono.value || '').trim();
      if (!email || !telefono) return;
      var btn = form.querySelector('button[type="submit"]');
      if (btn) btn.disabled = true;

      broqSubscribeKlaviyo({
        companyId: root.getAttribute('data-company-id'),
        listId: root.getAttribute('data-list-id'),
        email: email,
        phone: telefono,
        customSource: 'Cierre de colección',
        properties: {
          origen: 'cierre-coleccion',
          coleccion: window.location.pathname,
          marca: 'La Broqueleria'
        }
      })
        .then(function () {
          form.hidden = true;
          if (confirmacion) confirmacion.hidden = false;
        })
        .catch(function () {
          if (btn) btn.disabled = false;
        });
    });
  }

  var TROY_OZ_A_GRAMOS = 31.1034768;
  var PUREZA_10K = 10 / 24;

  function initGoldRibbon() {
    var root = document.querySelector('[data-broq-gold-ribbon]');
    if (!root || window.__broqGoldInit) return;
    window.__broqGoldInit = true;

    var texto = root.querySelector('[data-broq-gold-ribbon-texto]');
    if (!texto) return;

    function fmtMxn(n) {
      try { return '$' + n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 }); }
      catch (e) { return '$' + n.toFixed(2); }
    }

    function pintarFallback() {
      var precio = root.getAttribute('data-fallback-precio');
      var fecha = root.getAttribute('data-fallback-fecha');
      if (precio) {
        texto.innerHTML = 'Precio de referencia del oro 10k: <strong>' + fmtMxn(Number(precio)) + '/gramo</strong> · actualizado el ' + (fecha || 'recientemente');
      } else {
        texto.hidden = true; // sin fallback configurado: mejor no mostrar nada que mentir
      }
    }

    Promise.all([
      fetch('https://api.gold-api.com/price/XAU').then(function (r) { return r.json(); }),
      fetch('https://open.er-api.com/v6/latest/USD').then(function (r) { return r.json(); })
    ])
      .then(function (results) {
        var oro = results[0];
        var fx = results[1];
        var usdPorOnzaTroy24k = oro && oro.price;
        var usdMxn = fx && fx.rates && fx.rates.MXN;
        if (!usdPorOnzaTroy24k || !usdMxn) throw new Error('datos incompletos');

        var usdPorGramo24k = usdPorOnzaTroy24k / TROY_OZ_A_GRAMOS;
        var usdPorGramo10k = usdPorGramo24k * PUREZA_10K;
        var mxnPorGramo10k = usdPorGramo10k * usdMxn;

        var ahora = new Date().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
        texto.innerHTML = 'Precio del oro 10k hoy: <strong>' + fmtMxn(mxnPorGramo10k) + '/gramo</strong> · actualizado ' + ahora;
      })
      .catch(function () { pintarFallback(); });
  }

  function initPsvInfo() {
    if (window.__broqPsvInfoInit) return;
    window.__broqPsvInfoInit = true;
    var hover = window.matchMedia('(hover: hover)');
    var closeTimer;
    function close(except) {
      document.querySelectorAll('[data-broq-psv-info][open]').forEach(function (node) {
        if (node !== except) { node.open = false; delete node.dataset.pinned; }
      });
    }
    function position(node) {
      var body = node.querySelector('.broq-psv-info__body');
      if (!body) return;
      body.style.setProperty('--broq-psv-shift', '0px');
      var box = body.getBoundingClientRect();
      var shift = Math.max(12 - box.left, Math.min(0, window.innerWidth - 12 - box.right));
      body.style.setProperty('--broq-psv-shift', shift + 'px');
    }
    document.addEventListener('pointerover', function (event) {
      if (!hover.matches) return;
      var node = event.target.closest('[data-broq-psv-info]');
      if (!node) return;
      clearTimeout(closeTimer); close(node); node.open = true; position(node);
    });
    document.addEventListener('pointerout', function (event) {
      var node = event.target.closest('[data-broq-psv-info]');
      if (!node || node.contains(event.relatedTarget) || node.dataset.pinned) return;
      closeTimer = setTimeout(function () { node.open = false; }, 150);
    });
    document.addEventListener('focusin', function (event) {
      var node = event.target.closest('[data-broq-psv-info]');
      if (node && event.target.matches(':focus-visible')) { close(node); node.open = true; position(node); }
    });
    document.addEventListener('focusout', function (event) {
      var node = event.target.closest('[data-broq-psv-info]');
      if (node && !node.contains(event.relatedTarget)) { node.open = false; delete node.dataset.pinned; }
    });
    document.addEventListener('click', function (event) {
      var node = event.target.closest('[data-broq-psv-info]');
      close(node);
      if (!node || !event.target.closest('summary')) return;
      // Si hover o foco ya lo abrió, el primer clic lo conserva abierto.
      if (node.open && !node.dataset.pinned) { event.preventDefault(); node.dataset.pinned = 'true'; }
      else if (!node.open) { node.dataset.pinned = 'true'; }
      else { delete node.dataset.pinned; }
      requestAnimationFrame(function () { if (node.open) position(node); });
    });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') close();
    });
  }

  function init(root) {
    root = root && root.querySelectorAll ? root : document;

    // Header compacto, descarga de fotos y strips de precio: funcionales, corren siempre (incluso con PRM).
    initHeaderCompact();
    initHeaderQuizCta();
    initDownloadGate(root);
    initCollectionCierre();
    initGoldRibbon();
    initPsvInfo();
    initDiscountBadges(root);
    initVariantPriceStrips(root);
    if (document.querySelector('[data-broq-qa-control]')) initQuickAdd();
    initCartSwap();
    initCartProgress();
    initCollectionProgress();
    if (document.querySelector('[data-broq-kit-form]')) initKitAdd();
    if (document.querySelector('[data-broq-quiz]')) initQuiz();
    initCintillos(root);
    clearTimeout(window.__broqCintillosRetry);
    window.__broqCintillosRetry = setTimeout(function () { initCintillos(root); }, 600);
    initCorteEnvio();
    initOfertaCarrusel();
    initAnclaResenas();
    initPopupInfo();
    initPdpCartAutoOpen();
    initStickyWatch();
    if (document.querySelector('[data-broq-scratch]')) initPopupScratch();
    if (document.querySelector('[data-broq-armador]')) initArmador();
    // La galería (slideshow-slide / deferred-media) son custom elements que se
    // montan después de este init, así que el primer intento suele no encontrar
    // nada. Mismo refuerzo diferido que initCintillos.
    if (document.querySelector('deferred-media')) {
      initVideoAutoplay(root);
      if (!window.__broqVideoRetryInit) {
        window.__broqVideoRetryInit = true;
        setTimeout(function () { initVideoAutoplay(document); }, 700);
        setTimeout(function () { initVideoAutoplay(document); }, 2000);
        window.addEventListener('load', function () { initVideoAutoplay(document); }, { once: true });
        if (window.customElements && customElements.whenDefined) {
          customElements.whenDefined('deferred-media').then(function () {
            initVideoAutoplay(document);
          }).catch(function () {});
        }
      }
    }

    if (prefersReducedMotion()) {
      // No decorar, no observar, no sparkles: los elementos quedan
      // visibles de inmediato (incluidos los .broq-reveal del Liquid).
      revealEverything(root);
      return;
    }

    ensureObservers();
    autoDecorate(root);
    applyStagger(root);
    observeReveals(root);
    decorateSparkles(root);
  }

  function start() {
    // Señal para el CSS: solo ocultar .broq-reveal cuando el JS está activo.
    document.documentElement.classList.add('broq-motion-ready');
    init(document);
  }

  // Defer-safe: con `defer` el DOM ya está listo, pero cubrimos ambos casos.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  // Editor de temas de Shopify: re-inicializar la sección recargada.
  document.addEventListener('shopify:section:load', function (event) {
    var section = event && event.target ? event.target : document;
    // Permitir re-decorar sparkles de la sección recién renderizada.
    var stale = safeQueryAll(section, '[data-broq-sparkles-init]');
    for (var i = 0; i < stale.length; i++) {
      stale[i].removeAttribute('data-broq-sparkles-init');
    }
    init(section);
  });

  // Morph de Horizon en el storefront: al cambiar de variante en
  // combined listings (morfea <main>) o en featured-product (morfea
  // featured-product-information), morph.js reescribe class y data-*
  // con el HTML del servidor — borra .is-visible y data-broq-observed
  // y los .broq-reveal ya revelados volverían a opacity:0 para siempre.
  // El variant-picker despacha ProductSelectEvent (bubbles) ANTES del
  // fetch, con event.promise que resuelve DESPUÉS del morph: ahí
  // re-inicializamos (init es idempotente; re-observa lo que el morph
  // dejó sin data-broq-observed y re-revela lo que está en viewport).
  document.addEventListener('shopify:product:select', function (event) {
    var after = event && event.promise && typeof event.promise.then === 'function'
      ? event.promise
      : Promise.resolve();
    after
      .then(function () {
        requestAnimationFrame(function () { init(document); });
      })
      .catch(function () { /* fetch abortado: no hay morph que reparar */ });
  });

  // Morph de colección (ordenar / filtros / paginación): el sectionRenderer
  // reescribe nodos SIN pasar por shopify:product:select, y los banners de
  // arriba renacían en opacity:0 para siempre (reporte de Sergio 25-ago:
  // "ordené por más vendidos y desaparecieron los banners"). En vez de cazar
  // el nombre del evento de cada morph, un MutationObserver global (mismo
  // patrón que __broqCuObs/__broqQaObs) detecta reveals huérfanos —
  // .broq-reveal sin data-broq-observed, que es justo lo que el morph deja —
  // y re-corre init, que es idempotente y re-revela lo que está en viewport.
  if (!window.__broqRevealObs && 'MutationObserver' in window) {
    var revealRepairPending = false;
    window.__broqRevealObs = new MutationObserver(function () {
      if (revealRepairPending) return;
      if (!document.querySelector('.broq-reveal:not([data-broq-observed="true"]), [data-broq-kit-verification="required"]:not([data-broq-kit-verification-started])')) return;
      revealRepairPending = true;
      requestAnimationFrame(function () {
        revealRepairPending = false;
        init(document);
      });
    });
    window.__broqRevealObs.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'data-broq-observed'] });
  }

  // Si el usuario activa "reducir movimiento" en vivo, revelar todo.
  if (typeof reducedMotionQuery.addEventListener === 'function') {
    reducedMotionQuery.addEventListener('change', function (event) {
      if (event.matches) revealEverything(document);
    });
  } else if (typeof reducedMotionQuery.addListener === 'function') {
    // Safari viejo
    reducedMotionQuery.addListener(function (event) {
      if (event.matches) revealEverything(document);
    });
  }

  // API pública mínima para otros scripts del tema.
  window.BroqueleriaMotion = {
    __installed: true,
    init: init,
    refresh: function () { init(document); }
  };
})();
